// Super Agent — ACL-governed agent over the standard MCP proxy
import { Fragment, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { apiProxyUrl } from '../../lib/service-endpoints';
import { Card, CardContent, PageHeader, PriorityList, Select, Tab, TabList, TabPanel, Tabs } from '../../components/ui';
import {
  AlertTriangle,
  BookOpen,
  Bot,
  Brain,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock,
  FileEdit,
  FileText,
  GitBranch,
  Heart,
  History,
  Info,
  Key,
  List,
  Loader2,
  MessageSquare,
  MessageSquareOff,
  Paperclip,
  Play,
  Plus,
  RefreshCw,
  Search,
  Settings,
  Square,
  Terminal,
  Trash2,
  Wrench,
  XCircle,
  Zap,
  ZapOff,
} from 'lucide-react';
import { toast } from 'sonner';

// ── Typy ──────────────────────────────────────────────────────────────────────

interface SessionSummary {
  id: string;
  task: string;
  started_at: string;
  tokens: { prompt: number; completion: number };
  initiated_by: string;
  status: string;
}

interface AgentStatus {
  ok: boolean;
  idle: boolean;
  current_session: SessionSummary | null;
  current_session_user: SessionSummary | null;
  current_session_mcp: SessionSummary | null;
  allow_external: boolean;
  allow_chat: boolean;
}

interface Session {
  id: string;
  status: string;
  task: string;
  result: string | null;
  started_at: string;
  finished_at: string | null;
  model: string;
  initiated_by: string;
  prompt_tokens: number;
  completion_tokens: number;
}

interface TokenRow {
  endpoint_label: string;
  model: string;
  prompt_tokens: number;
  completion_tokens: number;
}

interface EndpointConfig {
  id: string;
  label: string;
  backend: 'anthropic' | 'openai';
  url: string;
  api_key: string;
  model: string;
  enabled: boolean;
  priority: number;
  token_cap: number; // 0 = unlimited
}

interface SaConfig {
  endpoints: EndpointConfig[];
  max_rounds: number;
  token_cap_total: number;
  thinking_budget: number;
  system_prompt: string;
  system_prompt_chat: string;
}

interface LlmMessage {
  role: string;
  content: unknown;
  tool_call_id?: string;
  tool_calls?: unknown[];
}

// ── Pomocné ───────────────────────────────────────────────────────────────────

function fmtTokens(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

type ToolCategory = 'file' | 'git' | 'shell' | 'search' | 'wiki' | 'other';
function toolCategory(name: string): ToolCategory {
  if (/^file_/.test(name)) return 'file';
  if (/^git_/.test(name)) return 'git';
  if (/^shell_exec/.test(name)) return 'shell';
  if (/^grep$|^search_files$/.test(name)) return 'search';
  if (/^wiki_/.test(name)) return 'wiki';
  return 'other';
}
const TOOL_CAT_ICON: Record<ToolCategory, typeof FileText> = {
  file: FileText,
  git: GitBranch,
  shell: Terminal,
  search: Search,
  wiki: BookOpen,
  other: Wrench,
};
const TOOL_CAT_COLOR: Record<ToolCategory, string> = {
  file: 'text-green-500',
  git: 'text-purple-500',
  shell: 'text-yellow-500',
  search: 'text-cyan-500',
  wiki: 'text-blue-400',
  other: 'text-gray-500',
};

function statusBadge(status: string) {
  const map: Record<string, string> = {
    running: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    done: 'bg-green-500/20 text-green-400 border-green-500/30',
    error: 'bg-red-500/20 text-red-400 border-red-500/30',
    aborted: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    idle: 'bg-gray-700/40 text-gray-400 border-gray-700/40',
  };
  return `inline-block px-2 py-0.5 rounded text-[10px] font-mono border ${
    map[status] ?? map['idle']
  }`;
}

function timeAgo(iso: string | null) {
  if (!iso) return '—';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h`;
}

// ── SSE event line ────────────────────────────────────────────────────────────

interface StreamLine {
  type: string;
  text?: string;
  name?: string;
  ok?: boolean;
  output?: string;
  error?: string;
  prompt?: number;
  completion?: number;
  endpoint?: string;
  context_tokens?: number;
  msg_count?: number;
  compacted?: number;
  remaining?: number;
  message?: string;
  result?: string;
  label?: string;
  from?: string;
  to?: string;
  // file tracker
  path?: string;
  op?: string;
}

// Merge consecutive text chunks into blocks for readable rendering
type OutputBlock =
  | { kind: 'text'; text: string }
  | { kind: 'event'; line: StreamLine; key: number };

function buildBlocks(lines: StreamLine[]): OutputBlock[] {
  const blocks: OutputBlock[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.type === 'text') {
      const last = blocks[blocks.length - 1];
      if (last?.kind === 'text') last.text += line.text ?? '';
      else blocks.push({ kind: 'text', text: line.text ?? '' });
    } else {
      blocks.push({ kind: 'event', line, key: i });
    }
  }
  return blocks;
}

function EventRow({ line }: { line: StreamLine }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  if (line.type === 'endpoint_start') {
    return (
      <div className='flex items-center gap-1.5 text-purple-400 text-xs mt-3 mb-1 border-t border-gray-800/60 pt-2'>
        <Zap size={10} />
        <span className='font-mono'>{line.label}</span>
      </div>
    );
  }
  if (line.type === 'endpoint_switch') {
    return (
      <div className='flex items-center gap-1.5 text-yellow-500 text-xs mt-1'>
        <AlertTriangle size={10} />
        <span>{t('superagent.fallbackLabel', { from: line.from, to: line.to, error: line.error })}</span>
      </div>
    );
  }
  if (line.type === 'tool_call') {
    return (
      <div className='flex items-center gap-1.5 text-blue-400 font-mono text-xs mt-2 py-0.5'>
        <Terminal size={11} />
        <span className='font-semibold'>{line.name}</span>
      </div>
    );
  }
  if (line.type === 'tool_result') {
    const hasDetail = line.output || line.error;
    const detail = (line.error ?? line.output ?? '').trim();
    return (
      <div className='mt-0.5 mb-1'>
        <div
          className={`flex items-center gap-1.5 text-xs font-mono ${
            line.ok ? 'text-green-400' : 'text-red-400'
          } ${hasDetail ? 'cursor-pointer select-none' : ''}`}
          onClick={() => hasDetail && setExpanded((e) => !e)}
        >
          {line.ok ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
          <span>{line.name}</span>
          {hasDetail && (
            <span className='text-[10px] text-gray-600 ml-1'>
              {expanded ? t('superagent.hideDetail') : t('superagent.showDetail')}
            </span>
          )}
        </div>
        {expanded && hasDetail && (
          <pre className='mt-1.5 text-[11px] text-gray-400 whitespace-pre-wrap break-words font-mono bg-gray-900/60 border border-gray-800 rounded-lg p-3 max-h-64 overflow-y-auto'>
            {detail.length > 3000 ? detail.slice(0, 3000) + t('superagent.truncatedSuffix') : detail}
          </pre>
        )}
      </div>
    );
  }
  if (line.type === 'usage') {
    return (
      <div className='flex items-center gap-2 text-[11px] text-gray-600 mt-1 mb-0.5'>
        <Zap size={9} />
        <span>
          {line.endpoint && <span className='text-gray-500 mr-1'>{line.endpoint} ·</span>}
          in: {fmtTokens(line.prompt ?? 0)} · out: {fmtTokens(line.completion ?? 0)}
        </span>
      </div>
    );
  }
  if (line.type === 'error') {
    return (
      <div className='flex items-start gap-1.5 text-red-400 text-sm mt-2 p-2 bg-red-500/5 border border-red-500/20 rounded-lg'>
        <AlertTriangle size={13} className='mt-0.5 flex-shrink-0' />
        <span className='break-words'>{line.message}</span>
      </div>
    );
  }
  if (line.type === 'done' || line.type === 'aborted') {
    return (
      <div className='flex items-center gap-1.5 text-gray-500 text-xs mt-3 border-t border-gray-800 pt-2'>
        <Info size={10} />
        <span>{line.type === 'done' ? t('superagent.doneLabel') : t('superagent.abortedLabel')}</span>
      </div>
    );
  }
  return null;
}

// ── Tab: Stav ─────────────────────────────────────────────────────────────────

// ── Live stream pro standalone session ───────────────────────────────────────

function LiveSessionStream({ sessionId, onAbort }: { sessionId: string; onAbort: () => void }) {
  const { t } = useTranslation();
  const [lines, setLines] = useState<StreamLine[]>([]);
  const [done, setDone] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ac = new AbortController();
    abortRef.current = ac;
    setLines([]);
    setDone(false);
    (async () => {
      try {
        const resp = await fetch(apiProxyUrl(`/apiext/superagent/stream/${sessionId}`), {
          credentials: 'include',
          signal: ac.signal,
        });
        if (!resp.ok || !resp.body) return;
        const reader = resp.body.getReader();
        const dec = new TextDecoder();
        let buf = '';
        while (true) {
          const { done: d, value } = await reader.read();
          if (d) break;
          buf += dec.decode(value, { stream: true });
          const parts = buf.split('\n');
          buf = parts.pop() ?? '';
          for (const line of parts) {
            if (!line.startsWith('data: ')) continue;
            const raw = line.slice(6).trim();
            if (raw === '[DONE]') break;
            try {
              const ev = JSON.parse(raw) as StreamLine;
              setLines((prev) => [...prev, ev]);
              if (
                ev.type === 'done' || ev.type === 'aborted' || ev.type === 'error' ||
                ev.type === 'task_done'
              ) setDone(true);
              setTimeout(() => {
                if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
              }, 16);
            } catch { /* skip */ }
          }
        }
      } catch { /* aborted */ }
    })();
    return () => ac.abort();
  }, [sessionId]);

  return (
    <div className='border border-gray-800 rounded-xl overflow-hidden'>
      <div className='flex items-center justify-between px-3 py-2 bg-gray-900/60 border-b border-gray-800'>
        <div className='flex items-center gap-2'>
          {!done && <Loader2 size={11} className='animate-spin text-blue-400' />}
          <span className='text-xs font-mono text-gray-400'>{sessionId.slice(-8)}</span>
          <span className='text-[10px] text-gray-600'>{t('superagent.liveLabel')}</span>
        </div>
        {!done && (
          <button
            onClick={() => {
              abortRef.current?.abort();
              onAbort();
            }}
            className='flex items-center gap-1 text-[10px] text-red-400 hover:text-red-300 border border-red-500/20 rounded px-2 py-0.5 transition-colors'
          >
            <Square size={8} className='fill-current' /> {t('superagent.stopButton')}
          </button>
        )}
      </div>
      <div ref={scrollRef} className='max-h-64 overflow-y-auto p-2 space-y-0.5 bg-gray-950/50'>
        {lines.length === 0 && <p className='text-xs text-gray-600 p-2'>{t('superagent.waitingEventsLabel')}</p>}
        {lines.map((line, i) => <EventRow key={i} line={line} />)}
      </div>
    </div>
  );
}

function TabStatus(
  { status, tokens, config }: {
    status: AgentStatus | undefined;
    tokens: TokenRow[] | undefined;
    config: SaConfig | undefined;
  },
) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const switchMut = useMutation({
    mutationFn: (body: { allow_external?: boolean; allow_chat?: boolean }) =>
      api.mcp('superagent.admin', { action: 'switches', ...body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sa-status'] }),
    onError: (e) => toast.error((e as Error).message),
  });
  const heartbeatMut = useMutation({
    mutationFn: () => api.mcp('superagent.admin', { action: 'healthcheck' }),
    onSuccess: () => {
      toast.success(t('superagent.heartbeatStartedToast'));
      qc.invalidateQueries({ queryKey: ['sa-status'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  if (!status) return <div className='text-gray-600 text-sm py-8 text-center'>{t('superagent.loadingLabel')}</div>;

  const allTokens = tokens ?? [];
  const totalIn = allTokens.reduce((a, r) => a + (r.prompt_tokens ?? 0), 0);
  const totalOut = allTokens.reduce((a, r) => a + (r.completion_tokens ?? 0), 0);

  return (
    <div className='space-y-4'>
      <Card>
        <CardContent>
          <div className='flex items-center justify-between mb-4'>
            <div className='flex items-center gap-3'>
              <div
                className={`w-2.5 h-2.5 rounded-full ${
                  status.idle ? 'bg-gray-600' : 'bg-blue-400 animate-pulse'
                }`}
              />
              <span className='font-semibold text-white text-sm'>
                {status.idle ? t('superagent.idleLabel') : t('superagent.runningLabel')}
              </span>
              {status.current_session && (
                <span className='text-xs text-gray-500 truncate max-w-xs'>
                  {status.current_session.task}
                </span>
              )}
            </div>
            <div className='flex items-center gap-1'>
              {status.idle && (
                <button
                  onClick={() => heartbeatMut.mutate()}
                  disabled={heartbeatMut.isPending}
                  title={t('superagent.healthCheckTooltip')}
                  className='p-1.5 rounded text-gray-600 hover:text-rose-400 transition-colors disabled:opacity-40'
                >
                  {heartbeatMut.isPending
                    ? <Loader2 size={13} className='animate-spin' />
                    : <Heart size={13} />}
                </button>
              )}
              <button
                onClick={() => qc.invalidateQueries({ queryKey: ['sa-status'] })}
                className='p-1.5 rounded text-gray-600 hover:text-gray-400 transition-colors'
              >
                <RefreshCw size={13} />
              </button>
            </div>
          </div>

          <div className='grid grid-cols-2 gap-3'>
            {[
              {
                key: 'allow_external',
                icon: status.allow_external ? Zap : ZapOff,
                label: t('superagent.llmApiCallsLabel'),
                color: 'green',
                val: status.allow_external,
              },
              {
                key: 'allow_chat',
                icon: status.allow_chat ? MessageSquare : MessageSquareOff,
                label: t('superagent.chatCommunicationLabel'),
                color: 'blue',
                val: status.allow_chat,
              },
            ].map(({ key, icon: Icon, label, color, val }) => (
              <div key={key} className='border border-gray-800 rounded-xl p-3'>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <Icon size={13} className={val ? `text-${color}-400` : 'text-gray-600'} />
                    <span className='text-xs text-gray-300'>{label}</span>
                  </div>
                  <button
                    onClick={() =>
                      switchMut.mutate(
                        { [key]: !val } as { allow_external?: boolean; allow_chat?: boolean },
                      )}
                    className={`w-9 h-5 rounded-full transition-colors relative ${
                      val
                        ? `bg-${color}-500/30 border border-${color}-500/40`
                        : 'bg-gray-700 border border-gray-600'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 w-4 h-4 rounded-full transition-all ${
                        val ? `left-4 bg-${color}-400` : 'left-0.5 bg-gray-500'
                      }`}
                    />
                  </button>
                </div>
                <p className='text-[10px] text-gray-600 mt-1.5'>
                  {val ? t('superagent.enabledLabel') : t('superagent.blockedLabel')}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Live stream — jen pro standalone (non-chat) session */}
      {status.current_session && (
        <Card>
          <CardContent>
            <h3 className='text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3'>
              {t('superagent.liveOutputHeader')}
            </h3>
            <LiveSessionStream
              sessionId={status.current_session.id}
              onAbort={() => {
                api.mcp('superagent.stop', {
                  session_id: status.current_session!.id,
                }).catch(() => {});
                qc.invalidateQueries({ queryKey: ['sa-status'] });
              }}
            />
          </CardContent>
        </Card>
      )}

      {(status.current_session || (config?.token_cap_total && config.token_cap_total > 0)) && (
        <Card>
          <CardContent>
            <h3 className='text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3'>
              {t('superagent.currentSessionTokensHeader')}
            </h3>
            {status.current_session
              ? (() => {
                const sess = status.current_session!;
                const total = (sess.tokens.prompt ?? 0) + (sess.tokens.completion ?? 0);
                const cap = config?.token_cap_total ?? 0;
                const pct = cap > 0 ? Math.min(100, Math.round(total / cap * 100)) : 0;
                return (
                  <div className='space-y-2'>
                    <div className='flex items-center justify-between text-xs'>
                      <span className='text-gray-500'>
                        {t('superagent.inLabel')} <span className='text-gray-300'>{fmtTokens(sess.tokens.prompt)}</span>
                      </span>
                      <span className='text-gray-500'>
                        {t('superagent.outLabel')}{' '}
                        <span className='text-gray-300'>{fmtTokens(sess.tokens.completion)}</span>
                      </span>
                      <span className='text-gray-500'>
                        {t('superagent.totalColonLabel')}{' '}
                        <span
                          className={cap > 0 && total >= cap * 0.9
                            ? 'text-red-400'
                            : 'text-gray-300'}
                        >
                          {fmtTokens(total)}
                          {cap > 0 ? ` / ${fmtTokens(cap)}` : ''}
                        </span>
                      </span>
                    </div>
                    {cap > 0 && (
                      <div className='h-1.5 bg-gray-800 rounded-full overflow-hidden'>
                        <div
                          className={`h-full rounded-full transition-all ${
                            pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-blue-500'
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    )}
                    {config?.endpoints?.filter((ep) => ep.token_cap > 0).map((ep) => (
                      <div key={ep.id} className='text-[10px] text-gray-600'>
                        {ep.label} cap: {fmtTokens(ep.token_cap)}
                      </div>
                    ))}
                  </div>
                );
              })()
              : (
                <p className='text-xs text-gray-600'>
                  {t('superagent.capTotalLabel', { value: fmtTokens(config?.token_cap_total ?? 0) })}
                </p>
              )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <h3 className='text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3'>
            {t('superagent.tokensTodayHeader')}
          </h3>
          {allTokens.length === 0
            ? <p className='text-xs text-gray-600'>{t('superagent.noActivityTodayLabel')}</p>
            : (
              <div className='space-y-2'>
                {allTokens.map((r, i) => (
                  <div key={i} className='flex items-center justify-between text-xs'>
                    <div>
                      <span className='text-gray-300'>{r.endpoint_label}</span>
                      <code className='text-[10px] text-gray-600 ml-2 font-mono'>{r.model}</code>
                    </div>
                    <div className='flex items-center gap-4 text-gray-500'>
                      <span>
                        {t('superagent.inLabel')} <span className='text-gray-300'>{fmtTokens(r.prompt_tokens)}</span>
                      </span>
                      <span>
                        {t('superagent.outLabel')} <span className='text-gray-300'>{fmtTokens(r.completion_tokens)}</span>
                      </span>
                    </div>
                  </div>
                ))}
                {allTokens.length > 1 && (
                  <div className='flex items-center justify-between text-xs border-t border-gray-800 pt-2 mt-2'>
                    <span className='text-gray-500'>{t('superagent.totalLabel')}</span>
                    <div className='flex items-center gap-4 text-gray-400'>
                      <span>
                        {t('superagent.inLabel')} <span className='text-white'>{fmtTokens(totalIn)}</span>
                      </span>
                      <span>
                        {t('superagent.outLabel')} <span className='text-white'>{fmtTokens(totalOut)}</span>
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Tab: Úkol ─────────────────────────────────────────────────────────────────

function MsgRow({ m, i }: { m: LlmMessage; i: number }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const role = m.role as string;
  if (role === 'system') return null; // system prompt is irrelevant noise

  const roleColor = role === 'assistant'
    ? 'text-blue-400 border-blue-800/40'
    : role === 'tool'
    ? 'text-green-400 border-green-800/40'
    : /* user */ 'text-yellow-400 border-yellow-800/40';

  const hasCalls = Array.isArray(m.tool_calls) && m.tool_calls.length > 0;
  const rawContent = typeof m.content === 'string'
    ? m.content
    : m.content
    ? JSON.stringify(m.content, null, 2)
    : '';

  // Tool result message — show collapsibly
  if (role === 'tool') {
    let pretty = rawContent;
    try {
      pretty = JSON.stringify(JSON.parse(rawContent), null, 2);
    } catch { /* keep raw */ }
    return (
      <div className={`border-l-2 pl-3 ${roleColor}`}>
        <button
          onClick={() => setOpen((o) => !o)}
          className='flex items-center gap-2 text-xs text-green-400 hover:text-green-300 w-full text-left py-0.5'
        >
          <span className='font-mono font-semibold'>{t('superagent.toolResultLabel')}</span>
          {m.tool_call_id && (
            <span className='text-[10px] text-gray-600 font-mono'>
              {String(m.tool_call_id).slice(-8)}
            </span>
          )}
          <span className='text-[10px] text-gray-600'>{open ? '▲' : '▼'}</span>
        </button>
        {open && (
          <pre className='mt-1 text-[11px] text-gray-400 whitespace-pre-wrap break-words font-mono bg-gray-900/50 border border-gray-800 rounded p-2 max-h-48 overflow-y-auto'>
            {pretty.length > 4000 ? pretty.slice(0, 4000) + '\n…' : pretty}
          </pre>
        )}
      </div>
    );
  }

  return (
    <div key={i} className={`border-l-2 pl-3 ${roleColor}`}>
      <span
        className={`text-xs font-mono font-semibold ${
          role === 'assistant' ? 'text-blue-400' : 'text-yellow-400'
        }`}
      >
        {role}
      </span>
      {hasCalls && (
        <div className='mt-1 space-y-0.5'>
          {(m.tool_calls as Array<{ id: string; function: { name: string; arguments: string } }>)
            .map((tc, j) => (
              <div key={j} className='flex items-center gap-1.5 text-xs text-purple-400 font-mono'>
                <Terminal size={10} />
                <span className='font-semibold'>{tc.function?.name}</span>
                {tc.function?.arguments && tc.function.arguments !== '{}' && (
                  <span className='text-[10px] text-gray-600 truncate max-w-sm'>
                    ({tc.function.arguments.slice(0, 120)}
                    {tc.function.arguments.length > 120 ? '…' : ''})
                  </span>
                )}
              </div>
            ))}
        </div>
      )}
      {rawContent && (
        <p className='mt-0.5 text-sm text-gray-300 whitespace-pre-wrap break-words leading-relaxed'>
          {rawContent.length > 1500 ? rawContent.slice(0, 1500) + t('superagent.truncatedSuffix') : rawContent}
        </p>
      )}
    </div>
  );
}

function MessageView({ sessionId }: { sessionId: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['sa-messages', sessionId],
    queryFn: () =>
      api.mcp<{ ok: boolean; messages: LlmMessage[] }>('superagent.messages', {
        action: 'list',
        session_id: sessionId,
      }),
    enabled: open,
    refetchInterval: open ? 2000 : false,
  });

  const msgs = (data?.messages ?? []).filter((m) => m.role !== 'system');

  return (
    <Card>
      <button
        onClick={() => setOpen((o) => !o)}
        className='w-full flex items-center justify-between px-4 py-3 text-xs font-semibold text-gray-400 hover:text-gray-200 transition-colors'
      >
        <span className='flex items-center gap-2'>
          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {t('superagent.conversationContentLabel')}
          {msgs.length > 0 && <span className='text-gray-600'>({t('superagent.messagesCountLabel', { count: msgs.length })})</span>}
          {!data && open && isLoading && (
            <span className='text-gray-600 text-[10px]'>{t('superagent.onlyAvailableRunningHint')}</span>
          )}
        </span>
        {isLoading && open && <Loader2 size={11} className='animate-spin text-gray-600' />}
      </button>
      {open && (
        <div className='border-t border-gray-800 px-4 pb-4 pt-3 space-y-3 max-h-[600px] overflow-y-auto'>
          {msgs.length === 0 && !isLoading && (
            <p className='text-xs text-gray-600 py-2'>
              {t('superagent.noMessagesHint')}
            </p>
          )}
          {msgs.map((m, i) => <MsgRow key={i} m={m} i={i} />)}
        </div>
      )}
    </Card>
  );
}

// ── Chat item types ───────────────────────────────────────────────────────────

type ChatItem =
  | { kind: 'user'; text: string; id: number }
  | { kind: 'agent_text'; text: string; id: number }
  | { kind: 'think'; text: string; id: number }
  | { kind: 'tool_call'; name: string; args: string; id: number }
  | { kind: 'tool_result'; name: string; ok: boolean; detail: string; id: number }
  | { kind: 'ask_user'; question: string; options?: string[]; id: number }
  | { kind: 'plan_proposal'; title: string; steps: string[]; id: number }
  | { kind: 'status'; text: string; variant: 'done' | 'aborted' | 'error' | 'waiting'; id: number }
  | { kind: 'usage'; prompt: number; completion: number; endpoint?: string; id: number }
  | { kind: 'task_divider'; summary: string; tested: boolean; id: number }
  | {
    kind: 'recovery';
    sessionId: string;
    task: string;
    hasWritten: boolean;
    toolsLog: string[];
    error?: string;
    id: number;
  }
  | { kind: 'divider'; id: number };

// ── Thinking block ────────────────────────────────────────────────────────────

function ThinkBlock({ text, streaming }: { text: string; streaming?: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className='my-1'>
      <button
        onClick={() => setOpen((o) => !o)}
        className='flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-400 transition-colors select-none'
      >
        <Brain
          size={11}
          className={streaming ? 'animate-pulse text-purple-500' : 'text-purple-400'}
        />
        <span className='text-[11px]'>{streaming ? t('superagent.thinkingActiveLabel') : t('superagent.thinkingLabel')}</span>
        <span className='text-[10px] text-gray-700'>{open ? t('superagent.hideLabel') : t('superagent.showLabel')}</span>
      </button>
      {open && (
        <pre className='mt-1.5 text-[11px] text-gray-500 whitespace-pre-wrap break-words font-mono bg-purple-950/20 border border-purple-900/30 rounded-lg p-3 max-h-64 overflow-y-auto leading-relaxed'>
          {text}
        </pre>
      )}
    </div>
  );
}

// ── File Tracker panel ────────────────────────────────────────────────────────

interface TrackedFile {
  path: string;
  op: 'read' | 'write' | 'list' | 'diff';
  count: number;
}

function FileTrackerPanel({ files }: { files: TrackedFile[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  if (files.length === 0) return null;
  const written = files.filter((f) => f.op === 'write');
  const read = files.filter((f) => f.op === 'read');
  const other = files.filter((f) => f.op !== 'write' && f.op !== 'read');
  return (
    <div className='border border-gray-800 rounded-xl overflow-hidden flex-shrink-0'>
      {/* Header — colored counters like Claude Code */}
      <button
        onClick={() => setOpen((o) => !o)}
        className='w-full flex items-center justify-between px-3 py-1.5 bg-gray-900/40 hover:bg-gray-900/60 transition-colors'
      >
        <div className='flex items-center gap-2'>
          <FileText size={10} className='text-gray-600' />
          <span className='text-[10px] text-gray-600'>{t('superagent.filesLabel')}</span>
          {written.length > 0 && (
            <span className='flex items-center gap-1 text-[10px] font-mono font-bold text-red-400'>
              <FileEdit size={9} className='text-red-500' />
              {written.length}
            </span>
          )}
          {read.length > 0 && (
            <span className='flex items-center gap-1 text-[10px] font-mono font-bold text-green-400'>
              <FileText size={9} className='text-green-500' />
              {read.length}
            </span>
          )}
          {other.length > 0 && (
            <span className='text-[10px] font-mono text-gray-600'>{other.length}</span>
          )}
        </div>
        <span className='text-[9px] text-gray-700'>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className='max-h-40 overflow-y-auto divide-y divide-gray-800/30'>
          {/* Writes — red */}
          {written.map((f, i) => (
            <div key={'w' + i} className='flex items-center gap-1.5 px-3 py-1 bg-red-950/10'>
              <FileEdit size={9} className='text-red-500 flex-shrink-0' />
              <span className='text-[10px] text-red-300 font-mono truncate flex-1' title={f.path}>
                {f.path.split('/').slice(-2).join('/')}
              </span>
              {f.count > 1 && <span className='text-[9px] text-red-700 font-mono'>{f.count}×</span>}
            </div>
          ))}
          {/* Reads — green */}
          {read.map((f, i) => (
            <div key={'r' + i} className='flex items-center gap-1.5 px-3 py-1'>
              <FileText size={9} className='text-green-600 flex-shrink-0' />
              <span
                className='text-[10px] text-green-400/80 font-mono truncate flex-1'
                title={f.path}
              >
                {f.path.split('/').slice(-2).join('/')}
              </span>
              {f.count > 1 && (
                <span className='text-[9px] text-green-800 font-mono'>{f.count}×</span>
              )}
            </div>
          ))}
          {/* Other (list, diff) */}
          {other.map((f, i) => {
            const Icon = f.op === 'list' ? List : f.op === 'diff' ? GitBranch : FileText;
            return (
              <div key={'o' + i} className='flex items-center gap-1.5 px-3 py-1'>
                <Icon size={9} className='text-gray-600 flex-shrink-0' />
                <span
                  className='text-[10px] text-gray-500 font-mono truncate flex-1'
                  title={f.path}
                >
                  {f.path.split('/').slice(-2).join('/')}
                </span>
                {f.count > 1 && (
                  <span className='text-[9px] text-gray-700 font-mono'>{f.count}×</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Chat item renderer ────────────────────────────────────────────────────────

function ChatItemRow({ item, onAskUserReply, onContinue }: {
  item: ChatItem;
  onAskUserReply: (text: string) => void;
  onContinue?: (task: string) => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [continueText, setContinueText] = useState('');

  if (item.kind === 'user') {
    return (
      <div className='flex justify-end'>
        <div className='max-w-[80%] rounded-2xl rounded-tr-sm bg-blue-600/20 border border-blue-500/20 px-4 py-2.5'>
          <p className='text-sm text-white whitespace-pre-wrap break-words'>{item.text}</p>
        </div>
      </div>
    );
  }

  if (item.kind === 'agent_text') {
    return (
      <div className='flex items-start gap-2.5'>
        <div className='w-6 h-6 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center flex-shrink-0 mt-0.5'>
          <Bot size={12} className='text-gray-400' />
        </div>
        <p className='flex-1 text-sm text-gray-200 whitespace-pre-wrap break-words leading-relaxed min-w-0'>
          {item.text}
        </p>
      </div>
    );
  }

  if (item.kind === 'think') {
    return <ThinkBlock text={item.text} />;
  }

  if (item.kind === 'tool_call') {
    // Barva + ikona podle typu operace
    const isRead = item.name === 'file_read';
    const isWrite = item.name === 'file_write' || item.name === 'file_edit';
    const isGit = item.name.startsWith('git_');
    const isShell = item.name === 'shell_exec';
    const isSearch = item.name === 'grep' || item.name === 'search_files' ||
      item.name === 'list_dir';
    const nameColor = isRead
      ? 'text-green-400'
      : isWrite
      ? 'text-red-400'
      : isGit
      ? 'text-purple-400'
      : isShell
      ? 'text-yellow-400'
      : isSearch
      ? 'text-cyan-400'
      : 'text-blue-400';
    const iconColor = isRead
      ? 'text-green-600'
      : isWrite
      ? 'text-red-600'
      : isGit
      ? 'text-purple-600'
      : isShell
      ? 'text-yellow-600'
      : isSearch
      ? 'text-cyan-600'
      : 'text-gray-600';
    const Icon = isWrite ? FileEdit : isRead ? FileText : Terminal;

    // Parsuj args — zobraz jen nejrelevantnější
    let argSummary = '';
    if (item.args && item.args !== '{}') {
      try {
        const parsed = JSON.parse(item.args);
        const entries = Object.entries(parsed);
        if (entries.length === 1) {
          const [, v] = entries[0];
          argSummary = String(v).slice(0, 80);
        } else {
          argSummary = entries.map(([k, v]) => `${k}=${String(v).slice(0, 40)}`).join(' ').slice(
            0,
            100,
          );
        }
      } catch {
        argSummary = item.args.slice(0, 100);
      }
    }
    return (
      <div className='flex items-center gap-2 pl-8 py-0.5 text-xs font-mono'>
        <Icon size={10} className={`${iconColor} flex-shrink-0`} />
        <span className={`font-semibold ${nameColor}`}>{item.name}</span>
        {argSummary && (
          <span className='text-gray-600 truncate'>
            {argSummary}
            {argSummary.length >= 80 ? '…' : ''}
          </span>
        )}
      </div>
    );
  }

  if (item.kind === 'tool_result') {
    const hasDetail = !!item.detail.trim();
    return (
      <div className='pl-8'>
        <button
          onClick={() => hasDetail && setExpanded((e) => !e)}
          className={`flex items-center gap-2 text-xs font-mono ${
            item.ok ? 'text-green-400' : 'text-red-400'
          } ${hasDetail ? 'cursor-pointer hover:opacity-80' : ''}`}
        >
          {item.ok ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
          <span>{item.name}</span>
          {hasDetail && (
            <span className='text-[10px] text-gray-600 ml-1'>
              {expanded ? t('superagent.hideDetail') : t('superagent.showDetail')}
            </span>
          )}
        </button>
        {expanded && hasDetail && (
          <pre className='mt-1.5 text-[11px] text-gray-400 whitespace-pre-wrap break-words font-mono bg-gray-900/60 border border-gray-800 rounded-lg p-3 max-h-48 overflow-y-auto'>
            {item.detail.length > 3000 ? item.detail.slice(0, 3000) + t('superagent.truncatedSuffix') : item.detail}
          </pre>
        )}
      </div>
    );
  }

  if (item.kind === 'plan_proposal') {
    return (
      <div className='flex items-start gap-2.5 my-2'>
        <div className='w-6 h-6 rounded-full bg-yellow-900/40 border border-yellow-500/30 flex items-center justify-center flex-shrink-0 mt-0.5'>
          <ChevronRight size={12} className='text-yellow-400' />
        </div>
        <div className='flex-1 rounded-xl border border-yellow-500/20 bg-yellow-900/5 p-4 space-y-3'>
          <div className='flex items-center gap-2'>
            <span className='text-xs font-semibold text-yellow-400 uppercase tracking-wider'>
              {t('superagent.planLabel')}
            </span>
            <span className='text-sm text-white font-medium'>{item.title}</span>
          </div>
          <ol className='space-y-1.5'>
            {item.steps.map((step, i) => (
              <li key={i} className='flex items-start gap-2.5 text-sm text-gray-300'>
                <span className='flex-shrink-0 w-5 h-5 rounded-full bg-gray-800 border border-gray-700 text-[10px] font-mono text-gray-500 flex items-center justify-center mt-0.5'>
                  {i + 1}
                </span>
                <span className='whitespace-pre-wrap'>{step}</span>
              </li>
            ))}
          </ol>
          <div className='flex items-center gap-2 pt-1'>
            <button
              onClick={() => {
                onAskUserReply(t('superagent.approvedValue'));
              }}
              className='flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-green-600/20 border border-green-500/30 text-green-300 hover:bg-green-600/40 transition-colors font-semibold'
            >
              <CheckCircle2 size={12} /> {t('superagent.approveButton')}
            </button>
            <button
              onClick={() => {
                onAskUserReply(t('superagent.rejectedValue'));
              }}
              className='flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-red-600/10 border border-red-500/20 text-red-400 hover:bg-red-600/20 transition-colors'
            >
              <XCircle size={12} /> {t('superagent.rejectButton')}
            </button>
            <div className='flex-1 flex gap-2'>
              <input
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && replyText.trim()) {
                    onAskUserReply(replyText.trim());
                    setReplyText('');
                  }
                }}
                placeholder={t('superagent.orWriteEditPlaceholder')}
                className='flex-1 bg-gray-900 border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-yellow-500'
              />
              {replyText.trim() && (
                <button
                  onClick={() => {
                    onAskUserReply(replyText.trim());
                    setReplyText('');
                  }}
                  className='px-2.5 py-1.5 text-xs rounded-lg bg-yellow-600/20 border border-yellow-500/30 text-yellow-300 hover:bg-yellow-600/40 transition-colors'
                >
                  {t('superagent.submitButton')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (item.kind === 'ask_user') {
    return (
      <div className='flex items-start gap-2.5 my-1'>
        <div className='w-6 h-6 rounded-full bg-purple-900/40 border border-purple-500/30 flex items-center justify-center flex-shrink-0 mt-0.5'>
          <MessageSquare size={11} className='text-purple-400' />
        </div>
        <div className='flex-1 rounded-xl border border-purple-500/20 bg-purple-900/10 p-3 space-y-2'>
          {item.question && <p className='text-sm text-gray-200'>{item.question}</p>}
          {item.options && item.options.length > 0 && (
            <div className='flex flex-wrap gap-1.5'>
              {item.options.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => onAskUserReply(opt)}
                  className='px-2.5 py-1 text-xs rounded-lg bg-purple-600/20 border border-purple-500/30 text-purple-300 hover:bg-purple-600/40 transition-colors'
                >
                  {opt}
                </button>
              ))}
            </div>
          )}
          <div className='flex gap-2'>
            <input
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && replyText.trim()) {
                  onAskUserReply(replyText.trim());
                  setReplyText('');
                }
              }}
              placeholder={t('superagent.replyToAgentPlaceholder')}
              className='flex-1 bg-gray-900 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-purple-500'
              autoFocus
            />
            <button
              onClick={() => {
                if (replyText.trim()) {
                  onAskUserReply(replyText.trim());
                  setReplyText('');
                }
              }}
              disabled={!replyText.trim()}
              className='px-3 py-1.5 text-xs rounded-lg bg-purple-600/30 border border-purple-500/30 text-purple-300 hover:bg-purple-600/50 disabled:opacity-40 transition-colors'
            >
              {t('superagent.submitButton')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (item.kind === 'status') {
    const colors = {
      done: 'text-green-400',
      aborted: 'text-yellow-500',
      error: 'text-red-400',
      waiting: 'text-blue-400',
    };
    return (
      <div
        className={`flex items-center gap-2 text-xs pl-8 border-t border-gray-800/50 pt-2 mt-1 ${
          colors[item.variant]
        }`}
      >
        <Info size={10} />
        <span>{item.text}</span>
      </div>
    );
  }

  if (item.kind === 'usage') {
    return (
      <div className='flex items-center gap-2 text-[10px] text-gray-500 pl-8'>
        <Zap size={9} className='text-yellow-600' />
        {item.endpoint && <span className='text-gray-600'>{item.endpoint} ·</span>}
        <span>
          <span className='text-blue-400/70'>{fmtTokens(item.prompt)}</span>
          <span className='text-gray-600'>in ·</span>
          <span className='text-green-400/70'>{fmtTokens(item.completion)}</span>
          <span className='text-gray-600'>out</span>
        </span>
      </div>
    );
  }

  if (item.kind === 'task_divider') {
    return (
      <div className='my-4'>
        <div className='flex items-center gap-2 mb-2'>
          <div className='flex-1 border-t border-green-800/40' />
          <span className='flex items-center gap-1.5 text-[10px] text-green-600 font-medium px-1'>
            <CheckCircle2 size={10} />
            {t('superagent.taskDoneLabel')}{item.tested ? ` ${t('superagent.testedSuffix')}` : ''}
          </span>
          <div className='flex-1 border-t border-green-800/40' />
        </div>
        {item.summary && (
          <div className='text-[11px] text-gray-500 text-center px-6 leading-relaxed'>
            {item.summary.slice(0, 500)}
          </div>
        )}
      </div>
    );
  }

  if (item.kind === 'recovery') {
    const donePrefix = item.hasWritten
      ? t('superagent.recoveryWrittenPrefix')
      : t('superagent.recoveryNotWrittenPrefix');
    return (
      <div className='mx-1 my-2 rounded border border-yellow-700/60 bg-yellow-950/30 p-3 text-xs'>
        <div className='font-semibold text-yellow-400 mb-1'>{t('superagent.sessionInterruptedTitle')}</div>
        <div className='text-gray-400 mb-1'>{donePrefix}</div>
        <div className='font-mono text-gray-500 mb-2 max-h-28 overflow-y-auto'>
          {item.toolsLog.map((l, i) => <div key={i}>{l}</div>)}
        </div>
        {item.error && <div className='text-red-400 mb-2'>{t('superagent.errorColonLabel', { error: item.error })}</div>}
        <textarea
          className='w-full rounded bg-gray-900 border border-gray-700 text-xs text-gray-200 p-2 mb-2 resize-none'
          rows={3}
          placeholder={t('superagent.recoveryContinuePlaceholder')}
          value={continueText}
          onChange={(e) => setContinueText(e.target.value)}
        />
        <button
          className='px-3 py-1 rounded bg-yellow-700 hover:bg-yellow-600 text-white text-xs disabled:opacity-40'
          disabled={!continueText.trim()}
          onClick={() =>
            onContinue?.(
              t('superagent.continuePromptTemplate', {
                sessionId: item.sessionId.slice(0, 8),
                task: item.task,
                continueText,
              }),
            )}
        >
          {t('superagent.continueInNewSessionButton')}
        </button>
      </div>
    );
  }

  if (item.kind === 'divider') {
    return <div className='border-t border-gray-800/40 my-1' />;
  }

  return null;
}

// ── Tab: Úkol (chat interface) ────────────────────────────────────────────────

const SA_SESSION_KEY = 'sa_chat_session';

function TabTask({ status }: { status: AgentStatus | undefined }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [items, setItems] = useState<ChatItem[]>([]);
  const [inputVal, setInputVal] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  // idle = no session, running = agent working, waiting = agent awaiting input
  const [inputState, setInputState] = useState<'idle' | 'running' | 'waiting'>('idle');
  const [planMode, setPlanMode] = useState<'off' | 'on' | 'auto'>('auto');
  const [thinkingOn, setThinkingOn] = useState(true);
  const [trackedFiles, setTrackedFiles] = useState<TrackedFile[]>([]);
  const [showAttach, setShowAttach] = useState(false);
  const [attachUploading, setAttachUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [contextTokens, setContextTokens] = useState(0);
  // ctx_max dynamicky z config — fallback 200k pokud není známý
  const [ctxMax, setCtxMax] = useState(200_000);
  const [msgCount, setMsgCount] = useState(0);
  const [agentRound, setAgentRound] = useState(0);
  const [lastCompacted, setLastCompacted] = useState(false);
  const [toolCounts, setToolCounts] = useState<Partial<Record<ToolCategory, number>>>({});
  const [totalIn, setTotalIn] = useState(0);
  const [totalOut, setTotalOut] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const idRef = useRef(0);
  const reconnectDone = useRef(false);

  // Upload souboru z disku → obsah inline do chatu (base64 přes MCP, žádný REST z UI)
  const handleFileUpload = async (file: File) => {
    setAttachUploading(true);
    try {
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let bin = '';
      const chunkSize = 0x8000;
      for (let i = 0; i < bytes.length; i += chunkSize) {
        bin += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
      }
      const content_base64 = btoa(bin);
      const data = await api.mcp<{
        ok: boolean;
        name?: string;
        content?: string;
        truncated?: boolean;
        error?: string;
      }>('superagent.file_upload', { name: file.name, content_base64 }, { silent: true });
      if (!data.ok) {
        toast.error(data.error ?? t('superagent.uploadFailedError'));
        return;
      }
      const ext = (data.name ?? '').split('.').pop() ?? '';
      const lang = {
        ts: 'typescript',
        tsx: 'tsx',
        js: 'javascript',
        jsx: 'jsx',
        py: 'python',
        json: 'json',
        yaml: 'yaml',
        yml: 'yaml',
        md: 'markdown',
        sh: 'bash',
        sql: 'sql',
        html: 'html',
        css: 'css',
      }[ext] ?? '';
      const truncNote = data.truncated ? t('superagent.fileTruncatedNote') : '';
      setInputVal((v) =>
        v + `\n\n[${t('superagent.fileHeaderLabel', { name: data.name })}]\n\`\`\`${lang}\n${data.content}\n\`\`\`${truncNote}`
      );
      setShowAttach(false);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setAttachUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Historie posledních chatů
  const { data: histSessions } = useQuery({
    queryKey: ['sa-sessions'],
    queryFn: () =>
      api.mcp<
        {
          ok: boolean;
          sessions: Array<{ id: string; task: string; status: string; started_at: string }>;
        }
      >('superagent.sessions', { action: 'list' }),
    refetchInterval: 30_000,
  });

  const nextId = () => ++idRef.current;

  const scrollToBottom = () => {
    setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, 16);
  };

  const addItem = (item: Omit<ChatItem, 'id'>) => {
    setItems((prev) => [...prev, { ...item, id: nextId() } as ChatItem]);
    scrollToBottom();
  };

  const processEvent = (ev: StreamLine) => {
    if (ev.type === 'text' && ev.text) {
      setItems((prev) => {
        const last = prev[prev.length - 1];
        if (last?.kind === 'agent_text') {
          return [...prev.slice(0, -1), { ...last, text: last.text + ev.text }];
        }
        return [...prev, { kind: 'agent_text', text: ev.text!, id: nextId() }];
      });
      scrollToBottom();
    } else if (ev.type === 'think' && ev.text) {
      addItem({ kind: 'think', text: ev.text });
    } else if (ev.type === 'file_track' && ev.path) {
      const path = ev.path;
      const op = (ev.op ?? 'read') as TrackedFile['op'];
      setTrackedFiles((prev) => {
        const existing = prev.find((f) => f.path === path && f.op === op);
        if (existing) return prev.map((f) => f === existing ? { ...f, count: f.count + 1 } : f);
        return [...prev, { path, op, count: 1 }];
      });
    } else if (ev.type === 'tool_call') {
      const cat = toolCategory(ev.name ?? '');
      setToolCounts((prev) => ({ ...prev, [cat]: (prev[cat] ?? 0) + 1 }));
      addItem({
        kind: 'tool_call',
        name: ev.name ?? '',
        args: (ev as { args?: string }).args ?? '',
      });
    } else if (ev.type === 'tool_result') {
      addItem({
        kind: 'tool_result',
        name: ev.name ?? '',
        ok: ev.ok ?? true,
        detail: ev.error ?? ev.output ?? '',
      });
    } else if (ev.type === 'plan_proposal') {
      addItem({
        kind: 'plan_proposal',
        title: (ev as { title?: string }).title ?? '',
        steps: (ev as { steps?: string[] }).steps ?? [],
      });
      setInputState('waiting');
    } else if (ev.type === 'ask_user') {
      addItem({
        kind: 'ask_user',
        question: (ev as { question?: string; message?: string }).question ??
          (ev as { message?: string }).message ?? '',
        options: (ev as { options?: string[] }).options,
      });
      setInputState('waiting');
    } else if (ev.type === 'waiting_input') {
      // Nezobrazujeme "Čeká na váš vstup" po dokončení úkolu — task_divider je dostatečný
      addItem({ kind: 'status', text: t('superagent.waitingInputStatus'), variant: 'waiting' });
      setInputState('waiting');
    } else if (ev.type === 'task_divider') {
      // Grafický oddělovač dokončeného úkolu (nahrazuje finish_approval dialog)
      const td = ev as { summary?: string; tested?: boolean };
      addItem({ kind: 'task_divider', summary: td.summary ?? '', tested: td.tested ?? false });
    } else if (ev.type === 'recovery_prompt') {
      const rp = ev as {
        session_id?: string;
        task?: string;
        has_written?: boolean;
        tools_log?: string[];
        error?: string;
      };
      addItem({
        kind: 'recovery',
        sessionId: rp.session_id ?? '',
        task: rp.task ?? '',
        hasWritten: rp.has_written ?? false,
        toolsLog: rp.tools_log ?? [],
        error: rp.error,
      });
    } else if (ev.type === 'task_done') {
      // Úkol splněn — chat zůstává živý, task_divider už byl zobrazen, jen resetujeme state
      setInputState('idle');
      qc.invalidateQueries({ queryKey: ['sa-status', 'sa-sessions', 'sa-tokens-today'] });
    } else if (ev.type === 'done') {
      // Skutečný konec session (timeout nečinnosti nebo abort)
      addItem({ kind: 'status', text: t('superagent.chatEndedStatus'), variant: 'done' });
      setInputState('idle');
      setSessionId(null);
      localStorage.removeItem(SA_SESSION_KEY);
      qc.invalidateQueries({ queryKey: ['sa-status', 'sa-sessions', 'sa-tokens-today'] });
    } else if (ev.type === 'aborted') {
      const reason = (ev as { reason?: string }).reason;
      const text = reason === 'server_restart'
        ? t('superagent.serverRestartedStatus')
        : t('superagent.abortedLabel');
      addItem({ kind: 'status', text, variant: 'aborted' });
      setInputState('idle');
      setSessionId(null);
      localStorage.removeItem(SA_SESSION_KEY);
      qc.invalidateQueries({ queryKey: ['sa-status', 'sa-sessions', 'sa-tokens-today'] });
    } else if (ev.type === 'text_full') {
      // Kompletní assistant text z event replay (náhrada za streaming chunky)
      if (ev.text) addItem({ kind: 'agent_text', text: ev.text as string });
    } else if (ev.type === 'replay_start') {
      // Začátek replaye eventů — přidej vizuální oddělovač
      addItem({
        kind: 'status',
        text: t('superagent.contextRestoredStatus', { count: ev.count ?? '?' }),
        variant: 'done',
      });
    } else if (ev.type === 'replay_end') {
      // Konec replaye — nic, jen marker
    } else if (ev.type === 'error') {
      const isNotFound = /nenalezena|not found/i.test(ev.message ?? '');
      addItem({
        kind: 'status',
        text: ev.message ?? t('superagent.abortedLabel'),
        variant: isNotFound ? 'aborted' : 'error',
      });
      if (isNotFound) {
        setInputState('idle');
        setSessionId(null);
        localStorage.removeItem(SA_SESSION_KEY);
      }
    } else if (ev.type === 'deploy_progress') {
      addItem({ kind: 'status', text: `⚙ ${ev.text ?? ev.step ?? ''}`, variant: 'done' });
    } else if (ev.type === 'checkpoint_saved') {
      addItem({
        kind: 'status',
        text: t('superagent.checkpointSavedStatus', { sha: (ev.sha ?? '').slice(0, 8) }),
        variant: 'done',
      });
    } else if (ev.type === 'compaction') {
      setLastCompacted(true);
      setTimeout(() => setLastCompacted(false), 4000);
      addItem({
        kind: 'status',
        text: t('superagent.compactionStatus', { compacted: ev.compacted ?? '?', remaining: ev.remaining ?? '?' }),
        variant: 'done',
      });
    } else if (ev.type === 'usage') {
      if (ev.context_tokens != null) setContextTokens(ev.context_tokens);
      const evCtxMax = (ev as { ctx_max?: number }).ctx_max;
      if (evCtxMax != null) setCtxMax(evCtxMax > 0 ? evCtxMax : 200_000);
      if (ev.msg_count) setMsgCount(ev.msg_count);
      if ((ev as { round?: number }).round) setAgentRound((ev as { round?: number }).round!);
      if (ev.prompt != null) setTotalIn(ev.prompt);
      if (ev.completion != null) setTotalOut(ev.completion);
      addItem({
        kind: 'usage',
        prompt: ev.prompt ?? 0,
        completion: ev.completion ?? 0,
        endpoint: ev.endpoint,
      });
    }
  };

  const startStream = (sid: string) => {
    localStorage.setItem(SA_SESSION_KEY, sid);
    const ac = new AbortController();
    abortRef.current = ac;
    (async () => {
      try {
        const resp = await fetch(apiProxyUrl(`/apiext/superagent/stream/${sid}`), {
          credentials: 'include',
          signal: ac.signal,
        });
        if (!resp.ok || !resp.body) {
          setInputState('idle');
          return;
        }
        const reader = resp.body.getReader();
        const dec = new TextDecoder();
        let buf = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const parts = buf.split('\n');
          buf = parts.pop() ?? '';
          for (const line of parts) {
            if (!line.startsWith('data: ')) continue;
            const raw = line.slice(6).trim();
            if (raw === '[DONE]') break;
            try {
              processEvent(JSON.parse(raw) as StreamLine);
            } catch { /* skip */ }
          }
        }
      } catch {
        /* aborted */
      } finally {
        setInputState((prev) => prev === 'running' ? 'idle' : prev);
        qc.invalidateQueries({ queryKey: ['sa-status', 'sa-sessions', 'sa-tokens-today'] });
      }
    })();
  };

  // Reconnect po refreshi — pokud server stále drží user session
  useEffect(() => {
    if (reconnectDone.current || sessionId) return;
    reconnectDone.current = true;
    const saved = localStorage.getItem(SA_SESSION_KEY);
    if (!saved) return;
    api.mcp<{ ok: boolean; session: { status: string; task: string } }>(
      'superagent.sessions',
      { action: 'get', session_id: saved },
    )
      .then((r) => {
        if (!r.ok) {
          localStorage.removeItem(SA_SESSION_KEY);
          return;
        }
        const st = r.session?.status;
        if (st === 'running' || st === 'waiting_input') {
          setSessionId(saved);
          setInputState(st === 'waiting_input' ? 'waiting' : 'running');
          addItem({
            kind: 'status',
            text: t('superagent.reconnectedStatus', { id: saved.slice(-8), task: r.session.task.slice(0, 60) }),
            variant: 'done',
          });
          startStream(saved);
        } else {
          localStorage.removeItem(SA_SESSION_KEY);
        }
      })
      .catch(() => localStorage.removeItem(SA_SESSION_KEY));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pokud server hlásí běžící user session a my ji nemáme — nabídneme reconnect
  const serverUserSession = status?.current_session_user ?? null;
  const canReconnectFromServer = !sessionId && !!serverUserSession &&
    (serverUserSession.status === 'running' || serverUserSession.status === 'waiting_input') &&
    !localStorage.getItem(SA_SESSION_KEY);

  const reconnectFromServer = () => {
    if (!serverUserSession) return;
    const sid = serverUserSession.id;
    localStorage.setItem(SA_SESSION_KEY, sid);
    setSessionId(sid);
    setInputState(serverUserSession.status === 'waiting_input' ? 'waiting' : 'running');
    addItem({
      kind: 'status',
      text: t('superagent.reconnectedStatus', { id: sid.slice(-8), task: serverUserSession.task.slice(0, 60) }),
      variant: 'done',
    });
    startStream(sid);
  };

  const handleSend = async () => {
    const text = inputVal.trim();
    if (!text) return;
    setInputVal('');

    // Když agent právě běží — injektuj do messageQueue (přeruší ho na příštím kole)
    if (inputState === 'running' && sessionId) {
      addItem({ kind: 'user', text });
      try {
        await api.mcp('superagent.messages', {
          action: 'post',
          session_id: sessionId,
          message: text,
        });
      } catch (e) {
        addItem({ kind: 'status', text: (e as Error).message, variant: 'error' });
      }
      return;
    }

    if (inputState === 'idle' && sessionId) {
      // Existující session — pošli zprávu do ní (po task_done, čeká na další úkol)
      setItems((prev) => [...prev, { kind: 'divider', id: nextId() }]);
      addItem({ kind: 'user', text });
      setInputState('running');
      try {
        await api.mcp('superagent.messages', {
          action: 'post',
          session_id: sessionId,
          message: text,
        });
      } catch (e) {
        addItem({ kind: 'status', text: (e as Error).message, variant: 'error' });
        setInputState('idle');
      }
    } else if (inputState === 'idle') {
      addItem({ kind: 'user', text });
      setInputState('running');
      setTrackedFiles([]);
      try {
        const r = await api.mcp<{ ok: boolean; session_id: string }>('superagent.run', {
          task: text,
          chat_mode: true,
          plan_mode: planMode,
          thinking: thinkingOn,
          initiated_by: 'user',
        });
        if (!r.ok) {
          addItem({ kind: 'status', text: t('superagent.startFailedStatus'), variant: 'error' });
          setInputState('idle');
          return;
        }
        setSessionId(r.session_id);
        startStream(r.session_id);
        qc.invalidateQueries({ queryKey: ['sa-status'] });
      } catch (e) {
        addItem({ kind: 'status', text: (e as Error).message, variant: 'error' });
        setInputState('idle');
      }
    } else if (inputState === 'waiting' && sessionId) {
      setItems((prev) => [...prev, { kind: 'divider', id: nextId() }]);
      scrollToBottom();
      addItem({ kind: 'user', text });
      setInputState('running');
      try {
        await api.mcp('superagent.messages', {
          action: 'post',
          session_id: sessionId,
          message: text,
        });
      } catch (e) {
        addItem({ kind: 'status', text: (e as Error).message, variant: 'error' });
        setInputState('waiting');
      }
    }
  };

  const handleContinue = async (task: string) => {
    if (inputState === 'running') return;
    setInputState('running');
    setTrackedFiles([]);
    addItem({ kind: 'divider', id: nextId() } as ChatItem);
    addItem({ kind: 'user', text: task });
    try {
      const r = await api.mcp<{ ok: boolean; session_id: string }>('superagent.run', {
        task,
        chat_mode: true,
        plan_mode: planMode,
        thinking: thinkingOn,
        initiated_by: 'user',
      });
      if (!r.ok) {
        addItem({ kind: 'status', text: t('superagent.startFailedStatus'), variant: 'error' });
        setInputState('idle');
        return;
      }
      setSessionId(r.session_id);
      startStream(r.session_id);
      qc.invalidateQueries({ queryKey: ['sa-status'] });
    } catch (e) {
      addItem({ kind: 'status', text: (e as Error).message, variant: 'error' });
      setInputState('idle');
    }
  };

  const handleAskUserReply = async (text: string) => {
    if (!sessionId) return;
    addItem({ kind: 'user', text });
    setInputState('running');
    try {
      await api.mcp('superagent.messages', {
        action: 'post',
        session_id: sessionId,
        message: text,
      });
    } catch (e) {
      addItem({ kind: 'status', text: (e as Error).message, variant: 'error' });
      setInputState('waiting');
    }
  };

  const handleAbort = () => {
    abortRef.current?.abort();
    if (sessionId) {
      api.mcp('superagent.stop', { session_id: sessionId }).catch(() => {});
    }
    addItem({ kind: 'status', text: t('superagent.abortedLabel'), variant: 'aborted' });
    setInputState('idle');
    setSessionId(null);
    localStorage.removeItem(SA_SESSION_KEY);
    qc.invalidateQueries({ queryKey: ['sa-status'] });
  };

  useEffect(() => () => {
    abortRef.current?.abort();
  }, []);

  // If an external session is running (e.g. after page reload), show indicator
  const externalSession = status?.current_session_mcp; // MCP/chat slot — jen info
  const placeholder = inputState === 'running'
    ? t('superagent.placeholderRunning')
    : inputState === 'waiting'
    ? t('superagent.placeholderWaiting')
    : planMode === 'on'
    ? t('superagent.placeholderPlanOn')
    : planMode === 'auto'
    ? t('superagent.placeholderPlanAuto')
    : t('superagent.placeholderIdle');

  return (
    <div className='flex flex-col' style={{ height: 'calc(100vh - 220px)', minHeight: '520px' }}>
      {/* Toolbar */}
      <div className='flex items-center justify-between mb-2 flex-shrink-0'>
        <div className='flex items-center gap-3 min-w-0'>
          {sessionId && (
            <span className='text-[10px] text-gray-700 font-mono flex-shrink-0'>
              {sessionId.slice(-8)}
            </span>
          )}

          {/* Context window bar — zobrazuje se při aktivní session */}
          {sessionId && contextTokens > 0 && (() => {
            const COMPACT_AT = Math.round(ctxMax * 0.6);
            const pct = Math.min(100, Math.round(contextTokens / ctxMax * 100));
            const compactPct = Math.min(100, Math.round(contextTokens / COMPACT_AT * 100));
            const barColor = lastCompacted
              ? 'bg-green-400'
              : contextTokens >= COMPACT_AT
              ? 'bg-red-400'
              : compactPct >= 70
              ? 'bg-yellow-300'
              : 'bg-blue-400';
            const textColor = lastCompacted
              ? 'text-green-300'
              : contextTokens >= COMPACT_AT
              ? 'text-red-300'
              : compactPct >= 70
              ? 'text-yellow-300'
              : 'text-gray-300';
            const maxLabel = ctxMax && ctxMax >= 1000
              ? `${(ctxMax / 1000).toFixed(0)}k`
              : String(ctxMax ?? '—');
            const tokLabel = contextTokens != null ? `${(contextTokens / 1000).toFixed(0)}k` : '—';
            return (
              <div
                className='flex items-center gap-2 flex-shrink-0'
                title={t('superagent.contextTooltip', {
                  used: contextTokens?.toLocaleString() ?? '?',
                  max: ctxMax?.toLocaleString() ?? '?',
                  pct,
                  compactAt: (COMPACT_AT / 1000).toFixed(0),
                  round: agentRound,
                })}
              >
                <div className='relative w-20 h-2 bg-gray-700 rounded-full overflow-hidden'>
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${barColor}`}
                    style={{ width: `${Math.max(pct, 2)}%` }}
                  />
                  <div
                    className='absolute top-0 bottom-0 w-px bg-gray-400 opacity-50'
                    style={{ left: '60%' }}
                  />
                </div>
                <span className={`text-[11px] font-mono tabular-nums font-medium ${textColor}`}>
                  {tokLabel}
                  <span className='text-gray-500'>/{maxLabel}</span>
                </span>
                {agentRound > 0 && (
                  <span className='text-[11px] font-mono text-gray-600'>#{agentRound}</span>
                )}
                {lastCompacted && <span className='text-[10px] text-green-400 font-medium'>↻</span>}
              </div>
            );
          })()}

          {(totalIn > 0 || totalOut > 0) && (
            <div
              className='flex items-center gap-1.5 flex-shrink-0'
              title={t('superagent.totalTokensTooltip', { in: totalIn.toLocaleString(), out: totalOut.toLocaleString() })}
            >
              <Zap size={10} className='text-yellow-500' />
              <span className='text-[11px] font-mono text-gray-400'>
                <span className='text-blue-400'>{fmtTokens(totalIn)}</span>
                <span className='text-gray-600'>/</span>
                <span className='text-green-400'>{fmtTokens(totalOut)}</span>
              </span>
            </div>
          )}

          {/* Tool countery — kategorie volání */}
          {Object.keys(toolCounts).length > 0 && (
            <div className='flex items-center gap-2 flex-shrink-0'>
              {(Object.entries(toolCounts) as [ToolCategory, number][])
                .sort((a, b) =>
                  b[1] - a[1]
                )
                .map(([cat, count]) => {
                  const Icon = TOOL_CAT_ICON[cat];
                  return (
                    <span
                      key={cat}
                      className={`flex items-center gap-0.5 text-[10px] font-mono tabular-nums ${
                        TOOL_CAT_COLOR[cat]
                      }`}
                      title={t('superagent.callsTooltip', { cat, count })}
                    >
                      <Icon size={9} />
                      {count}
                    </span>
                  );
                })}
            </div>
          )}
        </div>
        <div className='flex items-center gap-1'>
          <button
            onClick={() => setShowHistory((h) => !h)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg border transition-colors ${
              showHistory
                ? 'border-blue-600 text-blue-400'
                : 'border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-500'
            }`}
          >
            <History size={11} /> {t('superagent.historyButton')}
          </button>
          <button
            onClick={() => {
              abortRef.current?.abort();
              if (sessionId) {
                api.mcp('superagent.stop', { session_id: sessionId }).catch(() => {});
              }
              setItems([]);
              setSessionId(null);
              setInputVal('');
              setInputState('idle');
              setTrackedFiles([]);
              setContextTokens(0);
              setCtxMax(200_000);
              setMsgCount(0);
              setAgentRound(0);
              setToolCounts({});
              setTotalIn(0);
              setTotalOut(0);
              localStorage.removeItem(SA_SESSION_KEY);
            }}
            title={t('superagent.newChatTooltip')}
            className='flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg border border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-500 transition-colors'
          >
            <Plus size={11} /> {t('superagent.newChatButton')}
          </button>
        </div>
      </div>

      {/* Historie chatů */}
      {showHistory && (
        <div className='mb-3 rounded-xl border border-gray-800 bg-gray-900/60 overflow-hidden'>
          <div className='px-3 py-2 text-[10px] text-gray-500 border-b border-gray-800'>
            {t('superagent.last10ChatsLabel')}
          </div>
          {(histSessions?.sessions ?? []).slice(0, 10).map((s) => (
            <button
              key={s.id}
              className='w-full text-left px-3 py-2 text-xs hover:bg-gray-800 border-b border-gray-800/50 last:border-0 flex items-center gap-2'
              onClick={async () => {
                // Ukončit aktuální session
                if (sessionId) {
                  api.mcp('superagent.stop', { session_id: sessionId }).catch(() => {});
                }
                abortRef.current?.abort();
                setShowHistory(false);
                setTrackedFiles([]);
                // Načíst messages ze staré session
                try {
                  const r = await api.mcp<
                    { ok: boolean; messages: Array<{ role: string; content: unknown }> }
                  >('superagent.messages', { action: 'list', session_id: s.id });
                  const msgs = (r.messages ?? []).slice(-20); // posledních 20 zpráv
                  const newItems: ChatItem[] = msgs.flatMap((m): ChatItem[] => {
                    if (m.role === 'user' && typeof m.content === 'string') {
                      return [{ kind: 'user', text: m.content, id: nextId() }];
                    }
                    if (
                      m.role === 'assistant' && typeof m.content === 'string' && m.content
                    ) {
                      return [{ kind: 'agent_text', text: m.content, id: nextId() }];
                    }
                    return [];
                  });
                  setItems([{ kind: 'divider', id: nextId() }, ...newItems, {
                    kind: 'status',
                    text: t('superagent.restoredFromChatStatus', { task: s.task.slice(0, 60) }),
                    variant: 'done',
                    id: nextId(),
                  }]);
                  setSessionId(null);
                  setInputState('idle');
                  localStorage.removeItem(SA_SESSION_KEY);
                } catch {
                  setItems([]);
                  setSessionId(null);
                  setInputState('idle');
                }
              }}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                  s.status === 'done'
                    ? 'bg-green-500'
                    : s.status === 'running' || s.status === 'waiting_input'
                    ? 'bg-blue-500 animate-pulse'
                    : 'bg-gray-600'
                }`}
              />
              <span className='flex-1 truncate text-gray-300'>{s.task.slice(0, 70)}</span>
              <span className='text-gray-600 flex-shrink-0'>
                {new Date(s.started_at).toLocaleDateString('cs')}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Reconnect banner — server má aktivní user session, ale my ji nesledujeme */}
      {canReconnectFromServer && (
        <div className='mb-3 flex items-center gap-2 text-xs text-blue-400 bg-blue-500/10 border border-blue-500/20 rounded-xl px-3 py-2'>
          <Loader2 size={11} className='animate-spin flex-shrink-0' />
          <span className='flex-1'>
            {t('superagent.activeSessionLabel')}{' '}
            <code className='font-mono'>{serverUserSession!.task.slice(0, 60)}</code>
          </span>
          <button
            onClick={reconnectFromServer}
            className='px-2.5 py-1 rounded-lg bg-blue-600/30 border border-blue-500/40 text-blue-300 hover:bg-blue-600/50 transition-colors font-semibold whitespace-nowrap'
          >
            {t('superagent.reconnectButton')}
          </button>
        </div>
      )}

      {/* External MCP session warning */}
      {!sessionId && externalSession && inputState === 'idle' && (
        <div className='mb-3 flex items-center gap-2 text-xs text-purple-400 bg-purple-500/10 border border-purple-500/20 rounded-xl px-3 py-2'>
          <Bot size={11} className='flex-shrink-0' />
          <span>
            {t('superagent.agentChatSlotLabel')} <code className='font-mono'>{externalSession.task.slice(0, 80)}</code>
          </span>
        </div>
      )}

      {/* Chat area */}
      <div ref={scrollRef} className='flex-1 overflow-y-auto space-y-2 pb-2 pr-1'>
        {items.length === 0 && (
          <div className='flex flex-col items-center justify-center h-full text-gray-700 select-none'>
            <Bot size={36} className='mb-3 text-gray-800' />
            <p className='text-sm'>{t('superagent.emptyStateTitle')}</p>
            <p className='text-xs mt-1'>{t('superagent.emptyStateSubtitle')}</p>
          </div>
        )}
        {items.map((item) => (
          <ChatItemRow
            key={item.id}
            item={item}
            onAskUserReply={handleAskUserReply}
            onContinue={handleContinue}
          />
        ))}
        {inputState === 'running' && (
          <div className='flex items-center gap-2 text-xs text-gray-600 pl-8'>
            <Loader2 size={11} className='animate-spin' />
            <span>{t('superagent.agentWorkingLabel')}</span>
          </div>
        )}
      </div>

      {/* File tracker */}
      {trackedFiles.length > 0 && (
        <div className='flex-shrink-0 mt-1'>
          <FileTrackerPanel files={trackedFiles} />
        </div>
      )}

      {/* Input area */}
      <div className='border-t border-gray-800 pt-3 mt-2 flex-shrink-0 space-y-2'>
        {/* Session status row */}
        {sessionId && (
          <div className='flex items-center justify-between text-xs'>
            <div className='flex items-center gap-2'>
              {inputState === 'waiting' && (
                <span className='flex items-center gap-1.5 text-blue-400'>
                  <div className='w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse' />
                  {t('superagent.waitingForReplyLabel')}
                </span>
              )}
              {inputState === 'running' && (
                <span className='flex items-center gap-1.5 text-gray-600'>
                  <Loader2 size={10} className='animate-spin' />
                  {t('superagent.processingLabel')}
                </span>
              )}
            </div>
            <button
              onClick={handleAbort}
              className='flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-red-400 border border-red-500/20 hover:bg-red-500/10 transition-colors'
            >
              <Square size={10} /> {t('superagent.interruptButton')}
            </button>
          </div>
        )}

        {/* Mode selector — jen když idle nebo waiting */}
        {inputState !== 'running' && (
          <div className='flex items-center gap-1'>
            <span className='text-[10px] text-gray-700 mr-1'>{t('superagent.modeColonLabel')}</span>
            {([
              { id: 'off', label: t('superagent.modeChatLabel'), tip: t('superagent.modeChatTip') },
              { id: 'auto', label: t('superagent.modeAutoLabel'), tip: t('superagent.modeAutoTip') },
              { id: 'on', label: t('superagent.modePlanLabel'), tip: t('superagent.modePlanTip') },
            ] as const).map((m) => (
              <button
                key={m.id}
                onClick={() => setPlanMode(m.id)}
                title={m.tip}
                className={`px-2.5 py-1 text-xs rounded-lg transition-colors border ${
                  planMode === m.id
                    ? 'bg-blue-600/25 border-blue-500/40 text-blue-300'
                    : 'border-transparent text-gray-600 hover:text-gray-400 hover:border-gray-700'
                }`}
              >
                {m.label}
              </button>
            ))}
            <button
              onClick={() => setThinkingOn((v) => !v)}
              title={thinkingOn
                ? t('superagent.thinkingOnTooltip')
                : t('superagent.thinkingOffTooltip')}
              className={`flex items-center gap-1 px-2 py-1 text-xs rounded-lg transition-colors border ml-1 ${
                thinkingOn
                  ? 'bg-purple-600/25 border-purple-500/40 text-purple-300'
                  : 'border-transparent text-gray-600 hover:text-gray-400 hover:border-gray-700'
              }`}
            >
              <Brain size={10} /> {t('superagent.thinkingLabel')}
            </button>
            <div className='flex-1' />
            {/* Capability pills — vždy viditelné */}
            <div className='flex items-center gap-1 flex-wrap'>
              {[
                {
                  icon: '⎇',
                  label: 'git',
                  color: 'text-purple-400 border-purple-800/50 bg-purple-900/20',
                },
                {
                  icon: '📄',
                  label: t('superagent.filesCapabilityLabel'),
                  color: 'text-green-400  border-green-800/50  bg-green-900/20',
                },
                {
                  icon: '⚙',
                  label: 'shell',
                  color: 'text-yellow-400 border-yellow-800/50 bg-yellow-900/20',
                },
                {
                  icon: '🔍',
                  label: 'grep',
                  color: 'text-cyan-400   border-cyan-800/50   bg-cyan-900/20',
                },
                {
                  icon: '✏',
                  label: 'edit',
                  color: 'text-red-400    border-red-800/50    bg-red-900/20',
                },
              ].map((c) => (
                <span
                  key={c.label}
                  className={`text-[9px] border rounded px-1.5 py-0.5 font-mono ${c.color}`}
                >
                  {c.icon} {c.label}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Plan mode banner */}
        {planMode === 'on' && inputState === 'idle' && (
          <div className='flex items-center gap-2 text-[11px] text-yellow-600 bg-yellow-900/10 border border-yellow-800/30 rounded-lg px-3 py-1.5'>
            <ChevronRight size={11} />
            {t('superagent.planModeBannerText')}
          </div>
        )}

        {/* Attach file overlay */}
        {showAttach && (
          <div className='flex flex-col gap-2 bg-gray-900 border border-gray-700 rounded-xl px-3 py-2.5'>
            {/* Upload z disku */}
            <div className='flex items-center gap-2'>
              <Paperclip size={11} className='text-blue-400 flex-shrink-0' />
              <span className='text-xs text-gray-400'>{t('superagent.uploadFromComputerLabel')}</span>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={attachUploading}
                className='ml-auto text-xs px-2.5 py-1 rounded-lg bg-blue-600/20 border border-blue-500/40 text-blue-400 hover:bg-blue-600/30 disabled:opacity-50 transition-colors'
              >
                {attachUploading
                  ? (
                    <span className='flex items-center gap-1'>
                      <Loader2 size={10} className='animate-spin' /> {t('superagent.uploadingLabel')}
                    </span>
                  )
                  : t('superagent.selectFileButton')}
              </button>
              <input
                ref={fileInputRef}
                type='file'
                className='hidden'
                accept='.txt,.md,.ts,.tsx,.js,.jsx,.py,.json,.yaml,.yml,.sh,.sql,.html,.css,.env,.log,.csv,.xml,.toml'
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFileUpload(f);
                }}
              />
            </div>
          </div>
        )}

        {/* Textarea + send */}
        <div className='flex items-end gap-2'>
          <button
            onClick={() => setShowAttach((s) => !s)}
            title={t('superagent.attachFileTooltip')}
            className={`p-2.5 rounded-xl border transition-colors flex-shrink-0 ${
              showAttach
                ? 'bg-blue-600/20 border-blue-500/40 text-blue-400'
                : 'border-gray-700 text-gray-600 hover:text-gray-400 hover:border-gray-600'
            }`}
          >
            <Paperclip size={13} />
          </button>
          <textarea
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={inputState === 'running'
              ? t('superagent.messageQueuedPlaceholder')
              : placeholder}
            rows={2}
            className={`flex-1 bg-gray-900 border rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none resize-none transition-colors ${
              inputState === 'running'
                ? 'border-yellow-800 focus:border-yellow-600'
                : 'border-gray-700 focus:border-blue-500'
            }`}
          />
          <button
            onClick={handleSend}
            disabled={!inputVal.trim()}
            title={inputState === 'running' ? t('superagent.queueMessageTooltip') : t('superagent.submitButton')}
            className={`p-3 rounded-xl disabled:opacity-40 text-white transition-colors flex-shrink-0 ${
              inputState === 'running'
                ? 'bg-yellow-700 hover:bg-yellow-600'
                : 'bg-blue-600 hover:bg-blue-500'
            }`}
          >
            <Play size={14} />
          </button>
        </div>
        <p className='text-[10px] text-gray-700'>
          {inputState === 'running'
            ? t('superagent.footerHintRunning')
            : t('superagent.footerHintIdle')}
        </p>
      </div>
    </div>
  );
}

// ── Session trace — výpis zpráv ───────────────────────────────────────────────

function SessionTrace({ sessionId }: { sessionId: string }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: ['sa-messages', sessionId],
    queryFn: () =>
      api.mcp<{ ok: boolean; messages: LlmMessage[]; source: string }>('superagent.messages', {
        action: 'list',
        session_id: sessionId,
      }),
    staleTime: 30_000,
  });

  if (isLoading) {
    return <div className='text-gray-600 text-xs py-3 text-center'>{t('superagent.loadingHistoryLabel')}</div>;
  }
  if (error || !data?.ok) {
    return <div className='text-red-400 text-xs py-3'>{t('superagent.historyNotAvailableLabel')}</div>;
  }

  const messages = data.messages ?? [];

  return (
    <div className='space-y-2 max-h-[500px] overflow-y-auto pr-1'>
      {messages.map((m, i) => {
        const role = m.role;
        if (role === 'system') return null;

        // Asistent — text + tool calls
        if (role === 'assistant') {
          const text = typeof m.content === 'string'
            ? m.content.replace(/<scratchpad>[\s\S]*?<\/scratchpad>/g, '').replace(
              /<think>[\s\S]*?<\/think>/g,
              '',
            ).trim()
            : '';
          const toolCalls = Array.isArray(m.tool_calls)
            ? m.tool_calls as Array<
              { id: string; type: string; function: { name: string; arguments: string } }
            >
            : [];
          return (
            <div
              key={i}
              className='rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 space-y-2'
            >
              {text && (
                <p className='text-xs text-gray-300 whitespace-pre-wrap leading-relaxed'>{text}</p>
              )}
              {toolCalls.map((tc, j) => (
                <div key={j} className='flex items-start gap-2'>
                  <span className='text-[10px] font-mono bg-blue-900/40 text-blue-300 px-2 py-0.5 rounded whitespace-nowrap shrink-0'>
                    → {tc.function.name}
                  </span>
                  <pre className='text-[10px] text-gray-500 font-mono overflow-x-auto whitespace-pre-wrap break-all'>
                    {(() => { try { return JSON.stringify(JSON.parse(tc.function.arguments), null, 2); } catch { return tc.function.arguments; } })()}
                  </pre>
                </div>
              ))}
            </div>
          );
        }

        // Tool result
        if (role === 'tool') {
          const content = typeof m.content === 'string' ? m.content : JSON.stringify(m.content);
          const isError = content.startsWith('ERROR:');
          return (
            <div
              key={i}
              className={`rounded-lg border p-3 ${
                isError ? 'border-red-500/20 bg-red-500/5' : 'border-gray-700/40 bg-gray-900/30'
              }`}
            >
              <pre
                className={`text-[10px] font-mono whitespace-pre-wrap break-words ${
                  isError ? 'text-red-400' : 'text-gray-500'
                }`}
              >
                {content.slice(0, 2000)}{content.length > 2000 ? t('superagent.truncatedSuffix') : ''}
              </pre>
            </div>
          );
        }

        // User
        if (role === 'user') {
          const content = typeof m.content === 'string' ? m.content : JSON.stringify(m.content);
          return (
            <div key={i} className='rounded-lg border border-gray-700/40 bg-gray-900/20 p-3'>
              <p className='text-xs text-gray-400 whitespace-pre-wrap'>{content.slice(0, 500)}</p>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

// ── Tab: Historie ─────────────────────────────────────────────────────────────

function TabHistory() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['sa-sessions'],
    queryFn: () =>
      api.mcp<{ ok: boolean; sessions: Session[] }>('superagent.sessions', { action: 'list' }),
    refetchInterval: 10_000,
  });
  const [expanded, setExpanded] = useState<string | null>(null);
  const [traceOpen, setTraceOpen] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'user' | 'chat'>('all');

  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      api.mcp('superagent.sessions', { action: 'delete', session_id: id }),
    onSuccess: (_r, id) => {
      qc.invalidateQueries({ queryKey: ['sa-sessions'] });
      if (expanded === id) setExpanded(null);
      if (traceOpen === id) setTraceOpen(null);
      toast.success(t('superagent.sessionDeletedToast'));
    },
    onError: (e) => toast.error((e as Error).message),
  });

  if (isLoading) return <div className='text-gray-600 text-sm py-8 text-center'>{t('superagent.loadingLabel')}</div>;
  const allSessions = data?.sessions ?? [];
  const sessions = filter === 'all'
    ? allSessions
    : filter === 'chat'
    ? allSessions.filter((s) => s.initiated_by === 'chat' || s.initiated_by === 'mcp')
    : allSessions.filter((s) => s.initiated_by === filter);

  const colLabels: Record<string, string> = {
    time: t('superagent.colTime'),
    task: t('superagent.colTask'),
    status: t('superagent.colStatus'),
    tokens: t('superagent.colTokens'),
    initiatedBy: t('superagent.colInitiatedBy'),
  };

  return (
    <div className='space-y-2'>
      {/* Filtr */}
      <div className='flex items-center gap-1'>
        {(['all', 'user', 'chat'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1 text-xs rounded-lg transition-colors ${
              filter === f ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            {f === 'all'
              ? t('superagent.allCountLabel', { count: allSessions.length })
              : f === 'user'
              ? t('superagent.userCountLabel', { count: allSessions.filter((s) => s.initiated_by === 'user').length })
              : t('superagent.chatAgentCountLabel', {
                count: allSessions.filter((s) => s.initiated_by === 'chat' || s.initiated_by === 'mcp')
                  .length,
              })}
          </button>
        ))}
      </div>
      <div className='border border-gray-800 rounded-xl overflow-hidden'>
        {sessions.length === 0
          ? <div className='py-12 text-center text-gray-600 text-sm'>{t('superagent.noSessionsLabel')}</div>
          : (
            <table className='w-full text-xs'>
              <thead>
                <tr className='border-b border-gray-800 bg-gray-900/20'>
                  {[colLabels.time, colLabels.task, colLabels.status, colLabels.tokens, colLabels.initiatedBy, ''].map((h) => (
                    <th key={h} className='text-left px-4 py-2 text-gray-600 font-medium'>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-800/40'>
                {sessions.map((s) => (
                  <Fragment key={s.id}>
                    <tr className='hover:bg-gray-800/20'>
                      <td className='px-4 py-2.5 text-gray-500 whitespace-nowrap'>
                        <span className='flex items-center gap-1'>
                          <Clock size={10} />
                          {timeAgo(s.started_at)}
                        </span>
                      </td>
                      <td
                        className='px-4 py-2.5 text-gray-300 max-w-xs cursor-pointer'
                        onClick={() => setExpanded((e) => e === s.id ? null : s.id)}
                      >
                        <span className='truncate block'>
                          {s.task.slice(0, 80)}
                          {s.task.length > 80 ? '…' : ''}
                        </span>
                      </td>
                      <td className='px-4 py-2.5'>
                        <span className={statusBadge(s.status)}>{s.status}</span>
                      </td>
                      <td className='px-4 py-2.5 text-gray-500'>
                        {fmtTokens(s.prompt_tokens + s.completion_tokens)}
                      </td>
                      <td className='px-4 py-2.5'>
                        {s.initiated_by === 'chat'
                          ? (
                            <span className='inline-flex items-center gap-1 text-[10px] text-purple-400 bg-purple-500/10 border border-purple-500/20 px-1.5 py-0.5 rounded font-mono'>
                              <MessageSquare size={9} />chat
                            </span>
                          )
                          : (
                            <span className='text-[10px] text-gray-600 font-mono'>
                              {s.initiated_by}
                            </span>
                          )}
                      </td>
                      <td className='px-4 py-2.5'>
                        <div className='flex items-center gap-2 justify-end'>
                          <button
                            onClick={() => setTraceOpen((prev) => prev === s.id ? null : s.id)}
                            className='text-gray-600 hover:text-blue-400 transition-colors'
                            title={t('superagent.showProgressTooltip')}
                          >
                            <Terminal size={12} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteMut.mutate(s.id);
                            }}
                            disabled={deleteMut.isPending}
                            className='text-gray-700 hover:text-red-400 transition-colors'
                            title={t('superagent.deleteTooltip')}
                          >
                            <Trash2 size={12} />
                          </button>
                          <button
                            onClick={() => setExpanded((e) => e === s.id ? null : s.id)}
                            className='text-gray-600 hover:text-gray-400 transition-colors'
                          >
                            {expanded === s.id
                              ? <ChevronDown size={12} />
                              : <ChevronRight size={12} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expanded === s.id && s.result && (
                      <tr key={`${s.id}-result`} className='bg-gray-900/30'>
                        <td colSpan={6} className='px-4 py-3'>
                          <pre className='text-[11px] text-gray-400 whitespace-pre-wrap font-mono max-h-48 overflow-y-auto'>{s.result}</pre>
                        </td>
                      </tr>
                    )}
                    {traceOpen === s.id && (
                      <tr key={`${s.id}-trace`} className='bg-gray-950/50'>
                        <td colSpan={6} className='px-4 py-3'>
                          <div className='flex items-center justify-between mb-2'>
                            <span className='text-[10px] font-semibold text-gray-500 uppercase tracking-wider'>
                              {t('superagent.conversationProgressLabel')}
                            </span>
                            <code className='text-[9px] text-gray-700 font-mono'>{s.id}</code>
                          </div>
                          <SessionTrace sessionId={s.id} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
      </div>
    </div>
  );
}

// ── Endpoint karta ────────────────────────────────────────────────────────────

function EndpointEditorFields({
  ep,
  update,
}: {
  ep: EndpointConfig;
  update: (patch: Partial<EndpointConfig>) => void;
}) {
  const { t } = useTranslation();
  const [showKey, setShowKey] = useState(false);
  const [models, setModels] = useState<string[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [testing, setTesting] = useState(false);

  const testEndpoint = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.mcp<{ ok: boolean; status?: number; error?: string }>(
        'superagent.endpoints',
        { action: 'test', id: ep.id },
      );
      setTestResult(
        r.ok
          ? { ok: true, msg: `OK (${r.status})` }
          : { ok: false, msg: r.error ?? `HTTP ${r.status}` },
      );
    } catch (e) {
      setTestResult({ ok: false, msg: (e as Error).message });
    } finally {
      setTesting(false);
    }
  };

  if (ep.source === 'shared') {
    return (
      <div className='space-y-2'>
        <p className='text-xs text-gray-500'>
          {t('superagent.sharedProfileHintPre')}{' '}
          <a href='/modules/llm-profiles' className='text-blue-400 hover:underline'>
            {t('superagent.sharedProfileLinkText')}
          </a>.
        </p>
        <div className='grid grid-cols-2 gap-3 text-xs'>
          <div>
            <span className='text-gray-600'>{t('superagent.backendLabel')}</span>
            <div className='font-mono text-gray-300'>{ep.backend}</div>
          </div>
          <div>
            <span className='text-gray-600'>{t('superagent.modelLabel')}</span>
            <div className='font-mono text-gray-300'>{ep.model || t('superagent.defaultValueLabel')}</div>
          </div>
          <div className='col-span-2'>
            <span className='text-gray-600'>{t('superagent.urlLabel')}</span>
            <div className='font-mono text-gray-300 truncate'>{ep.url}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-3'>
      <label className='block'>
        <span className='text-[10px] text-gray-600 mb-1 block'>{t('superagent.labelFieldLabel')}</span>
        <input
          value={ep.label}
          onChange={(e) => update({ label: e.target.value })}
          className='w-full bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500'
        />
      </label>

      <div className='flex items-center justify-between'>
        <button
          type='button'
          onClick={testEndpoint}
          disabled={testing}
          className='flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 transition-colors disabled:opacity-40'
        >
          {testing ? <Loader2 size={11} className='animate-spin' /> : <CheckCircle2 size={11} />}
          {t('superagent.testButton')}
        </button>
        {testResult && (
          <span
            className={`text-xs px-2 py-0.5 rounded border max-w-xs text-right ${
              testResult.ok
                ? 'text-green-400 border-green-500/30 bg-green-500/10'
                : 'text-red-400 border-red-500/30 bg-red-500/10'
            }`}
          >
            {testResult.msg}
          </span>
        )}
      </div>

      <div className='grid grid-cols-2 gap-3'>
        <label className='block'>
          <span className='text-[10px] text-gray-600 mb-1 block'>{t('superagent.backendLabel')}</span>
          <Select
            size='sm'
            value={ep.backend}
            onChange={(e) => update({ backend: e.target.value as 'anthropic' | 'openai' })}
            className='w-full'
          >
            <option value='anthropic'>Anthropic</option>
            <option value='openai'>OpenAI-compat</option>
          </Select>
        </label>
        <label className='block'>
          <span className='text-[10px] text-gray-600 mb-1 flex items-center justify-between'>
            <span>{t('superagent.modelLabel')}</span>
            <button
              type='button'
              onClick={async () => {
                setLoadingModels(true);
                setModels([]);
                try {
                  const r = await api.mcp<{ ok: boolean; models: string[] }>(
                    'superagent.endpoints',
                    { action: 'models', id: ep.id },
                  );
                  setModels(r.models ?? []);
                } catch (e) {
                  setModels([]);
                  alert((e as Error).message);
                } finally {
                  setLoadingModels(false);
                }
              }}
              className='flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors'
            >
              {loadingModels
                ? <Loader2 size={9} className='animate-spin' />
                : <RefreshCw size={9} />}
              {t('superagent.loadButton')}
            </button>
          </span>
          {models.length > 0
            ? (
              <Select
                size='sm'
                value={ep.model}
                onChange={(e) => update({ model: e.target.value })}
                className='w-full font-mono'
              >
                {!models.includes(ep.model) && ep.model && (
                  <option value={ep.model}>{ep.model}</option>
                )}
                {models.map((m) => <option key={m} value={m}>{m}</option>)}
              </Select>
            )
            : (
              <input
                value={ep.model}
                onChange={(e) => update({ model: e.target.value })}
                className='w-full bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white font-mono focus:outline-none focus:border-blue-500'
                placeholder='claude-sonnet-4-5'
              />
            )}
        </label>
        <label className='col-span-2 block'>
          <span className='text-[10px] text-gray-600 mb-1 block'>{t('superagent.endpointUrlLabel')}</span>
          <input
            value={ep.url}
            onChange={(e) => update({ url: e.target.value })}
            className='w-full bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white font-mono focus:outline-none focus:border-blue-500'
            placeholder={ep.backend === 'anthropic'
              ? 'https://api.anthropic.com'
              : 'https://10.118.200.18:8004/v1'}
          />
        </label>
        <label className='block'>
          <span className='text-[10px] text-gray-600 mb-1 block'>{t('superagent.tokenCapSessionLabel')}</span>
          <input
            type='number'
            min='0'
            value={ep.token_cap ?? 0}
            onChange={(e) => update({ token_cap: parseInt(e.target.value) || 0 })}
            className='w-full bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500'
          />
        </label>
        <label className='block'>
          <span className='text-[10px] text-gray-600 mb-1 block'>{t('superagent.ctxWindowLabel')}</span>
          <input
            type='number'
            min='0'
            step={1000}
            value={ep.ctx_max ?? 0}
            onChange={(e) => update({ ctx_max: parseInt(e.target.value) || 0 })}
            className='w-full bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500'
          />
        </label>
        <label className='col-span-2 block'>
          <span className='text-[10px] text-gray-600 mb-1 flex items-center gap-1.5'>
            <Key size={9} />{t('superagent.apiKeyLabel')}
            {ep.api_key === '***' && <span className='text-green-400'>{t('superagent.setLabel')}</span>}
          </span>
          <div className='flex items-center gap-2'>
            <input
              type={showKey ? 'text' : 'password'}
              value={ep.api_key === '***' ? '' : ep.api_key}
              onChange={(e) => update({ api_key: e.target.value })}
              placeholder={ep.api_key === '***' ? t('superagent.keepExistingPlaceholder') : 'sk-...'}
              className='flex-1 bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white font-mono focus:outline-none focus:border-blue-500'
            />
            <button
              onClick={() => setShowKey((s) => !s)}
              className='text-[10px] text-gray-500 hover:text-gray-300 px-2 py-1.5 bg-gray-800 border border-gray-700 rounded-lg transition-colors'
            >
              {showKey ? t('superagent.hideWordLabel') : t('superagent.showWordLabel')}
            </button>
          </div>
        </label>
      </div>
    </div>
  );
}
// ── Prompt tabs — samostatná práce vs chat ────────────────────────────────────

function PromptTabs({
  standalone,
  chat,
}: {
  standalone: { value: string; onChange: (v: string) => void };
  chat: { value: string; onChange: (v: string) => void };
}) {
  const { t } = useTranslation();
  const [active, setActive] = useState<'standalone' | 'chat'>('standalone');

  const STANDALONE_PLACEHOLDER = t('superagent.standalonePlaceholder', { task: '{{task}}' });

  const CHAT_PLACEHOLDER = t('superagent.chatPlaceholder');

  const active_field = active === 'standalone' ? standalone : chat;
  const placeholder = active === 'standalone' ? STANDALONE_PLACEHOLDER : CHAT_PLACEHOLDER;
  const label = active === 'standalone' ? t('superagent.standaloneLabel') : t('superagent.chatLabel');
  const desc = active === 'standalone'
    ? t('superagent.standaloneDesc')
    : t('superagent.chatDesc');

  return (
    <div className='space-y-3'>
      <div className='flex items-center justify-between'>
        <h3 className='text-xs font-semibold text-gray-400 uppercase tracking-wider'>
          {t('superagent.systemPromptHeader')}
        </h3>
        <div className='flex items-center gap-1 bg-gray-900 rounded-xl p-1 border border-gray-800'>
          {(['standalone', 'chat'] as const).map((tb) => (
            <button
              key={tb}
              onClick={() => setActive(tb)}
              className={`px-3 py-1 text-xs rounded-lg transition-colors ${
                active === tb ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {tb === 'standalone' ? t('superagent.standaloneIconLabel') : t('superagent.chatIconLabel')}
            </button>
          ))}
        </div>
      </div>
      <p className='text-[11px] text-gray-600'>{desc}</p>
      <div className='flex items-center justify-between'>
        <span className='text-[10px] text-gray-700'>
          {t('superagent.variableLabel')} <code className='text-yellow-600'>{'{{task}}'}</code> {t('superagent.insertsTaskHint')}
        </span>
        {active_field.value && (
          <button
            onClick={() => active_field.onChange('')}
            className='text-[10px] text-gray-700 hover:text-red-400 transition-colors'
          >
            {t('superagent.restoreDefaultButton')}
          </button>
        )}
      </div>
      <textarea
        key={active}
        value={active_field.value}
        onChange={(e) => active_field.onChange(e.target.value)}
        rows={10}
        placeholder={placeholder}
        className='w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-2.5 text-xs text-white font-mono focus:outline-none focus:border-blue-500 resize-y'
      />
      <p className='text-[10px] text-gray-600'>
        {t('superagent.emptyFieldHint')}
      </p>
    </div>
  );
}

// ── Tab: Nastavení ────────────────────────────────────────────────────────────

function TabSettings() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: cfg } = useQuery({
    queryKey: ['sa-config'],
    queryFn: () => api.mcp<SaConfig & { ok: boolean }>('superagent.config_get', {}),
  });

  const [endpoints, setEndpoints] = useState<EndpointConfig[] | null>(null);
  const [maxRounds, setMaxRounds] = useState('10');
  const [tokenCapTotal, setTokenCapTotal] = useState('0');
  const [thinkingBudget, setThinkingBudget] = useState('0');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [systemPromptChat, setSystemPromptChat] = useState('');
  const [dirty, setDirty] = useState(false);

  // Sync only when server data changes (not on dirty toggle — avoids reset before refetch completes)
  useEffect(() => {
    if (!cfg || dirty) return;
    setEndpoints(cfg.endpoints ?? []);
    setMaxRounds(String(cfg.max_rounds));
    setTokenCapTotal(String(cfg.token_cap_total ?? 0));
    setThinkingBudget(String(cfg.thinking_budget ?? 0));
    setSystemPrompt(cfg.system_prompt ?? '');
    setSystemPromptChat(cfg.system_prompt_chat ?? '');
  }, [cfg]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.mcp('superagent.config_set', body),
    onSuccess: () => {
      toast.success(t('superagent.configSavedToast'));
      qc.invalidateQueries({ queryKey: ['sa-config'] });
      setDirty(false);
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const saveEndpoint = (ep: EndpointConfig) => {
    setEndpoints((prev) => (prev ?? []).map((e) => (e.id === ep.id ? ep : e)));
    setDirty(true);
  };
  const toggleEndpoint = (id: string, enabled: boolean) => {
    setEndpoints((prev) => (prev ?? []).map((e) => (e.id === id ? { ...e, enabled } : e)));
    setDirty(true);
  };
  const reorderEndpoints = (orderedIds: string[]) => {
    setEndpoints((prev) => {
      const byId = new Map((prev ?? []).map((e) => [e.id, e]));
      return orderedIds.map((id, i) => ({ ...byId.get(id)!, priority: i }));
    });
    setDirty(true);
  };
  const deleteEndpoint = (id: string) => {
    setEndpoints((prev) => (prev ?? []).filter((e) => e.id !== id));
    setDirty(true);
  };
  const addEndpoint = () => {
    const id = `ep_${Date.now()}`;
    setEndpoints((prev) => [
      ...(prev ?? []),
      {
        id,
        label: t('superagent.newEndpointDefaultLabel'),
        backend: 'openai' as const,
        url: '',
        api_key: '',
        model: '',
        enabled: false,
        priority: (prev ?? []).length,
        token_cap: 0,
        ctx_max: 0,
        source: 'own' as const,
      },
    ]);
    setDirty(true);
  };

  const save = () => {
    // Seřadit podle priority před uložením, pak přečíslovat
    const sorted = [...(endpoints ?? [])].sort((a, b) => a.priority - b.priority).map((ep, i) => ({
      ...ep,
      priority: i,
    }));
    saveMut.mutate({
      endpoints: sorted,
      max_rounds: maxRounds,
      token_cap_total: tokenCapTotal,
      thinking_budget: thinkingBudget,
      system_prompt: systemPrompt,
      system_prompt_chat: systemPromptChat,
    });
  };

  if (!endpoints) return <div className='text-gray-600 text-sm py-8 text-center'>{t('superagent.loadingLabel')}</div>;

  return (
    <div className='space-y-4 max-w-2xl'>
      {/* Endpointy */}
      <Card>
        <CardContent>
          <div className='flex items-center justify-between mb-4'>
            <h3 className='text-xs font-semibold text-gray-400 uppercase tracking-wider'>
              {t('superagent.llmEndpointsHeader')}
            </h3>
            <span className='text-[10px] text-gray-600'>
              {t('superagent.orderPriorityHint')}
            </span>
          </div>
          <PriorityList<EndpointConfig>
            items={endpoints}
            getTitle={(ep) => ep.label}
            getSubtitle={(ep) => (
              <span className='font-mono'>{ep.backend} · {ep.model || t('superagent.noModelLabel')}</span>
            )}
            renderEditor={(ep, update) => <EndpointEditorFields ep={ep} update={update} />}
            renderBadges={(ep) => (
              ep.source === 'shared'
                ? (
                  <span className='text-[10px] px-1.5 py-0.5 rounded text-blue-400 bg-blue-950/40'>
                    {t('superagent.mainListBadge')}
                  </span>
                )
                : null
            )}
            onSave={saveEndpoint}
            onDelete={deleteEndpoint}
            onToggle={toggleEndpoint}
            onReorder={reorderEndpoints}
            onAdd={addEndpoint}
            addLabel={t('superagent.addEndpointLabel')}
            emptyMessage={t('superagent.noEndpointsMessage')}
          />
        </CardContent>
      </Card>

      {/* Agent nastavení */}
      <Card>
        <CardContent>
          <h3 className='text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4'>
            {t('superagent.agentHeader')}
          </h3>
          <div className='grid grid-cols-2 gap-3'>
            <label className='block'>
              <span className='text-[10px] text-gray-600 mb-1 block'>
                {t('superagent.maxRoundsLabel')}
              </span>
              <input
                type='number'
                min='0'
                max='100'
                value={maxRounds}
                onChange={(e) => {
                  setMaxRounds(e.target.value);
                  setDirty(true);
                }}
                className='w-full bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500'
              />
            </label>
            <label className='block'>
              <span className='text-[10px] text-gray-600 mb-1 block'>
                {t('superagent.tokenCapTotalLabel')}
              </span>
              <input
                type='number'
                min='0'
                value={tokenCapTotal}
                onChange={(e) => {
                  setTokenCapTotal(e.target.value);
                  setDirty(true);
                }}
                placeholder='0'
                className='w-full bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500'
              />
            </label>
            <label className='block'>
              <span className='text-[10px] text-gray-600 mb-1 flex items-center gap-1'>
                <Brain size={9} className='text-purple-400' />{t('superagent.thinkingBudgetLabel')}
              </span>
              <input
                type='number'
                min='0'
                step='1000'
                value={thinkingBudget}
                onChange={(e) => {
                  setThinkingBudget(e.target.value);
                  setDirty(true);
                }}
                placeholder='0'
                className='w-full bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500'
              />
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Systémové prompty — dvě záložky */}
      <Card>
        <CardContent>
          <PromptTabs
            standalone={{
              value: systemPrompt,
              onChange: (v) => {
                setSystemPrompt(v);
                setDirty(true);
              },
            }}
            chat={{
              value: systemPromptChat,
              onChange: (v) => {
                setSystemPromptChat(v);
                setDirty(true);
              },
            }}
          />
        </CardContent>
      </Card>

      <div className='flex items-center gap-3'>
        <button
          onClick={save}
          disabled={!dirty || saveMut.isPending}
          className='flex items-center gap-2 px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-sm font-semibold text-white transition-colors'
        >
          {saveMut.isPending ? <Loader2 size={14} className='animate-spin' /> : null}
          {t('superagent.saveConfigButton')}
        </button>
        {dirty && <span className='text-xs text-yellow-500'>{t('superagent.unsavedChangesLabel')}</span>}
      </div>
    </div>
  );
}

// ── Hlavní komponenta ─────────────────────────────────────────────────────────

export default function SuperAgentPage() {
  const { t } = useTranslation();
  const { data: status } = useQuery({
    queryKey: ['sa-status'],
    queryFn: () => api.mcp<AgentStatus>('superagent.status', {}),
    refetchInterval: 5_000,
  });
  const { data: tokensData } = useQuery({
    queryKey: ['sa-tokens-today'],
    queryFn: () =>
      api.mcp<{ ok: boolean; by_endpoint: TokenRow[] }>('superagent.tokens_today', {}),
    refetchInterval: 30_000,
  });
  const { data: configData } = useQuery({
    queryKey: ['sa-config'],
    queryFn: () => api.mcp<SaConfig & { ok: boolean }>('superagent.config_get', {}),
    staleTime: 60_000,
  });

  return (
    <div className='space-y-6'>
      <PageHeader
        icon={<Bot size={20} className='text-blue-400' />}
        title={t('superagent.pageTitle')}
        description={t('superagent.pageDescription')}
        actions={status
          ? (
            <span className={statusBadge(status.idle ? 'idle' : 'running')}>
              {status.idle ? 'idle' : 'running'}
            </span>
          )
          : undefined}
      />

      <Tabs defaultTab='status'>
        <TabList>
          <Tab id='status'>
            <span className='flex items-center gap-1.5'>
              <Zap size={13} />{t('superagent.tabStatus')}
            </span>
          </Tab>
          <Tab id='task'>
            <span className='flex items-center gap-1.5'>
              <Terminal size={13} />{t('superagent.tabChat')}
            </span>
          </Tab>
          <Tab id='history'>
            <span className='flex items-center gap-1.5'>
              <History size={13} />{t('superagent.tabHistory')}
            </span>
          </Tab>
          <Tab id='settings'>
            <span className='flex items-center gap-1.5'>
              <Settings size={13} />{t('superagent.tabSettings')}
            </span>
          </Tab>
        </TabList>
        <TabPanel id='status'>
          <TabStatus status={status} tokens={tokensData?.by_endpoint} config={configData} />
        </TabPanel>
        <TabPanel id='task' keepMounted>
          <TabTask status={status} />
        </TabPanel>
        <TabPanel id='history'>
          <TabHistory />
        </TabPanel>
        <TabPanel id='settings'>
          <TabSettings />
        </TabPanel>
      </Tabs>
    </div>
  );
}
